/**
 * MaterialTakeoff — the per-job material takeoff the techs work from
 * (replaces the "Material Takeoff" Google Sheet). Lives under the Quotes tab.
 *
 * • One sheet per job, auto-created + seeded when an ElecBid quote converts.
 * • Spreadsheet-style grid mirroring the old sheet's columns, plus custom
 *   columns/rows, status chips (ordered / stock / backorder / …) and a
 *   location column (whose van / sea can / site / on hold @ supplier).
 * • Supplier selector per row + "Price from supplier" bulk re-price (only
 *   confident pricebook matches are applied — everything else keeps its
 *   current supplier/price).
 * • Order flow: select rows → email the branch (sent FROM the tech's own
 *   @browning-electric.ca mailbox), text City Electric Salmon Arm via the Quo
 *   line, or log a phone order — all tracked in material_orders and noted on
 *   the Jobber job.
 * • Supplier invoices auto-link back (expense webhook + daily sweep) and land
 *   per-item ACTUAL costs beside the quoted ones.
 *
 * All data access goes through /api/takeoff (service key server-side) — the
 * takeoff tables have no anon/authenticated grants.
 */
import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import { usePermissions } from "../hooks/usePermissions";
import { apiFetch } from "../lib/apiFetch";
import { supabase } from "../lib/supabase";
import { cleanCatalogDescription } from "../lib/materialText";
import { useCellRange, rangeToText, RangePill } from "../lib/useCellRange";
import FlowStrip from "./FlowStrip";

const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  lime: "#a5ce3a", text: "#e8eaf0", muted: "#8892a4", red: "#ef4444",
  amber: "#f59e0b", blue: "#60a5fa", green: "#34d399", purple: "#c084fc", teal: "#2dd4bf",
};

// Colour carries the workflow: BRIGHT = still on you, dark = done and out of
// the way. "needed" is the loudest thing on the grid on purpose — it's the
// work that hasn't been sourced yet — and "received" goes black so the rows
// you've already dealt with stop competing for attention.
// `text` overrides the label colour where the chip colour itself is too dark
// to read against.
const STATUSES = [
  { key: "needed",         label: "Needed",            color: "#fde047" },
  { key: "dont_order",     label: "Don't order yet",   color: "#f87171" },
  { key: "ready_to_order", label: "Ready to order",    color: C.amber },
  { key: "ordered",        label: "Ordered",           color: C.blue },
  { key: "backorder",      label: "On backorder",      color: "#fb923c" },
  { key: "stock",          label: "Stock",             color: C.green },
  { key: "check_van",      label: "Check sea can/van", color: C.purple },
  { key: "ready_for_ops",  label: "Ready for ops",     color: C.lime },
  { key: "received",       label: "Received",          color: "#0b0d10", text: "#9aa6b8" },
  { key: "installed",      label: "Installed",         color: "#64748b" },
];
const statusOf = (k) => STATUSES.find(s => s.key === k) || STATUSES[0];
// Statuses that mean "the material has physically arrived somewhere" — these
// prompt for a location, because that is the one moment anybody actually knows.
const LOCATED_STATUSES = new Set(["received", "stock", "installed"]);

// ── filter / search / highlight primitives ───────────────────────────────────
const NO_SUPPLIER = "__none__";   // the "nobody has sourced this yet" bucket
const ANY_COLOR   = "__any__";
const HL_KEY      = "_hl";        // row highlight, stored in the row's custom jsonb

// Lime is deliberately absent: it already means section-header and row-selected.
const HL_COLORS = [
  { hex: "#60a5fa", label: "Blue" },   { hex: "#c084fc", label: "Purple" },
  { hex: "#2dd4bf", label: "Teal" },   { hex: "#fb923c", label: "Orange" },
  { hex: "#f87171", label: "Rose" },   { hex: "#f59e0b", label: "Amber" },
  { hex: "#34d399", label: "Green" },  { hex: "#64748b", label: "Slate" },
];
// Whitelist gate: `custom` is client-authoritative and full-row upserted, so a
// hand-edited value must never reach an inline style prop.
const HL_HEXES = new Set(HL_COLORS.map(c => c.hex));
const hlOf = (it) => { const h = (it.custom || {})[HL_KEY]; return h && HL_HEXES.has(h) ? h : null; };
const hexA = (hex, a) => {
  const h = String(hex || "").replace("#", "");
  const n = parseInt(h.length === 3 ? h.split("").map(c => c + c).join("") : h, 16);
  return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
};
const normSup = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");

const SEARCH_FIELDS = ["description", "purpose", "part_number", "sku", "supplier", "notes", "location", "unit"];

// "14-2" → "14/2" BEFORE separators are stripped, so wire sizes survive.
const normText = (s) => String(s == null ? "" : s).toLowerCase()
  .replace(/(\d)\s*[-–]\s*(\d)/g, "$1/$2")
  .replace(/[^a-z0-9/#.]+/g, " ")
  .replace(/\s+/g, " ").trim();

// Tech shorthand ⇄ catalogue words. This is the tuning knob — extend freely.
const SEARCH_SYNONYMS = [
  ["receptacle", "recep", "plug", "outlet"], ["gfci", "gfi"], ["breaker", "brkr", "cb"],
  ["conduit", "emt", "pipe"], ["teck", "acwu"], ["connector", "conn", "fitting", "ftg"],
  ["panel", "panelboard", "loadcentre", "loadcenter"], ["disconnect", "disco"],
  ["luminaire", "light", "fixture", "pot"], ["switch", "sw"], ["junction", "jbox"],
  ["transformer", "xfmr"], ["romex", "nmd90", "nmd"], ["weatherproof", "wp"],
  ["generator", "genny", "genset"], ["nipple", "nip"], ["bushing", "bush"],
];
const SYN_INDEX = (() => { const m = new Map(); for (const g of SEARCH_SYNONYMS) for (const w of g) m.set(w, g); return m; })();

// Bounded Levenshtein — one rolling row, bail the moment the row minimum
// exceeds `max`. Only ever called on short alphabetic tokens.
function levLE(a, b, max) {
  const al = a.length, bl = b.length;
  if (Math.abs(al - bl) > max) return false;
  let prev = new Array(bl + 1);
  for (let j = 0; j <= bl; j++) prev[j] = j;
  for (let i = 1; i <= al; i++) {
    const cur = new Array(bl + 1);
    cur[0] = i;
    let rowMin = cur[0];
    const ca = a.charCodeAt(i - 1);
    for (let j = 1; j <= bl; j++) {
      const cost = ca === b.charCodeAt(j - 1) ? 0 : 1;
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      if (cur[j] < rowMin) rowMin = cur[j];
    }
    if (rowMin > max) return false;
    prev = cur;
  }
  return prev[bl] <= max;
}

// A digit-bearing token matches only on a digit boundary, so "2" cannot hit
// the "2" inside "12/2" and "15" cannot hit "150".
function digitHit(tok, words) {
  return words.some(w => {
    if (w === tok) return true;
    const i = w.indexOf(tok);
    if (i < 0) return false;
    return !/\d/.test(w[i - 1] || "") && !/\d/.test(w[i + tok.length] || "");
  });
}

function tokenHitsField(tok, field, words) {
  // SAFETY: any token containing a DIGIT is exact-only, word-bounded, and
  // compared against ONE field. Never fuzz a size or rating — 14/2 vs 12/2,
  // 15A vs 45A, #6 vs #8 are DIFFERENT MATERIAL at a different price.
  if (/\d/.test(tok)) return digitHit(tok, words);
  if (field.includes(tok)) return true;
  const grp = SYN_INDEX.get(tok);
  if (grp && grp.some(w => field.includes(w))) return true;
  if (tok.length < 4) return false;               // too short to fuzz safely
  const max = tok.length <= 6 ? 1 : 2;
  return words.some(w => Math.abs(w.length - tok.length) <= max && levLE(w, tok, max));
}

// Per-FIELD haystacks — never one concatenated row. Concatenating lets "14/2"
// match a description ending "…14" abutting a part number starting "2".
function rowHays(it) {
  const out = SEARCH_FIELDS.map(f => normText(it[f]));
  for (const [k, v] of Object.entries(it.custom || {})) if (!k.startsWith("_")) out.push(normText(v));
  return out.filter(Boolean).map(h => ({ h, w: h.split(" ") }));
}

// AND across tokens, fuzzy WITHIN a token, any single field may satisfy a token.
function rowMatches(it, tokens) {
  if (!tokens.length) return true;
  const hays = rowHays(it);
  return tokens.every(t => hays.some(({ h, w }) => tokenHitsField(t, h, w)));
}

// One box for both jobs: reaching a supplier who isn't in the directory, and
// telling the crew what's ordered. "kyle@browning-electric.ca, 250 555 1234"
// → { emails, phones, bad }. The email test mirrors api/send-email.js so the
// client rejects exactly what the server would; numbers are NANP-only because
// guessing a country code sends the text into a void.
function parseRecipients(s) {
  const emails = [], phones = [], bad = [];
  for (const raw of String(s || "").split(/[,;\n]/).map(x => x.trim()).filter(Boolean)) {
    if (raw.includes("@")) { (/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(raw) ? emails : bad).push(raw); continue; }
    const d = raw.replace(/\D/g, "");
    if (d.length === 10) phones.push("+1" + d);
    else if (d.length === 11 && d[0] === "1") phones.push("+" + d);
    else bad.push(raw);
  }
  return { emails: emails.slice(0, 5), phones: phones.slice(0, 5), bad };
}

const LOCATION_SUGGESTIONS = [
  "Shop", "Sea can", "Trailer", "Site", "On site in garage", "Installed",
  "Kyle's van", "Tommy's van", "Justin's van", "Morgan's van", "Landon's van", "Colton's van", "Pippin's van",
  "On hold @ City Electric", "On hold @ EB Horsman", "On hold @ Wesco",
];

async function api(action, payload = {}) {
  const r = await apiFetch("/api/takeoff", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action, ...payload }),
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}
// Absolute clock time, not "2 minutes ago" — no ticking timer to keep alive,
// and it can't quietly go stale while the tab sits in the background.
const fmtClock = (d) => {
  try { return d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", second: "2-digit" }); }
  catch { return ""; }
};
function money(n) {
  if (n == null || isNaN(+n)) return "—";
  return "$" + (+n).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
// Log Book / quote-backend formulas (verified against real backend rows):
//   Our cost each (incl PST) = unit_cost × (1 + PST%)
//   Client each              = our-cost-each × (1 + markup%)
//   Client total             = qty × client each · Profit = client − our cost
// 🚨 Round the PST-baked each-cost to the cent BEFORE the qty multiply. This is
// what recomputeTotals / api/quote-brain applyTotals do, and what the quote's
// materials table does — leaving it unrounded here made the SAME line read a
// few cents differently on the two screens under one shared "PST in" toggle,
// which is exactly the inconsistency that toggle exists to prevent.
const ourEach     = (it) => +((Number(it.unit_cost) || 0) * (1 + (Number(it.pst_pct) || 0) / 100)).toFixed(2);
const ourTotalOf  = (it) => (Number(it.qty) || 0) * ourEach(it);
// EX-TAX line total — qty x the price as typed, which is what a supplier quote
// or invoice LINE reads. It is the DEFAULT display basis (compare straight
// across, no mental tax-stripping) AND the only fair basis to measure the
// "Actual $" column against: those figures come from scanned invoice LINE
// totals, which are ex-tax (the extractor keeps subtotal / gst_amount /
// pst_amount separate — see api/_invoiceExtraction.js).
const ourExTotal  = (it) => (Number(it.qty) || 0) * (Number(it.unit_cost) || 0);
// BC charges GST and PST each on the PRE-TAX amount — they do not compound.
const TK_GST_PCT = 5;
const MAT_TAX_KEY = "elecbid-mat-tax";   // shared with the quote's materials table
// GST is an input tax credit: it comes back to us, so it only ever changes what
// the figure LOOKS like next to an invoice, never profit or margin.
const ourShownTotal = (it, basis) => {
  const ex = ourExTotal(it);
  return (basis && basis.pst ? ourTotalOf(it) : ex) + (basis && basis.gst ? ex * (TK_GST_PCT / 100) : 0);
};
const tkBasisLabel = (basis) => (basis && basis.pst
  ? (basis.gst ? "PST + GST in" : "PST-in")
  : (basis && basis.gst ? "GST in" : "ex tax"));
const clientEach  = (it) => ourEach(it) * (1 + (Number(it.markup_pct) || 0) / 100);
const clientTotalOf = (it) => (Number(it.qty) || 0) * clientEach(it);

// ── shared small styles ───────────────────────────────────────────────────────
const inputStyle = {
  width: "100%", boxSizing: "border-box", background: "transparent", border: "1px solid transparent",
  borderRadius: 6, color: C.text, fontSize: 12.5, padding: "5px 6px", outline: "none", fontFamily: "inherit",
};
const btn = (primary = false, danger = false) => ({
  background: primary ? C.lime : danger ? "rgba(239,68,68,.12)" : "transparent",
  border: `1px solid ${primary ? C.lime : danger ? "rgba(239,68,68,.5)" : C.border}`,
  borderRadius: 8, color: primary ? "#0d0f12" : danger ? "#f87171" : C.text,
  fontWeight: 700, fontSize: 12.5, padding: "7px 13px", cursor: "pointer", whiteSpace: "nowrap",
});
const chip = (color) => ({
  display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11.5, fontWeight: 700,
  color, background: color + "22", border: `1px solid ${color}55`, whiteSpace: "nowrap",
});

// ── work pad + text view helpers ─────────────────────────────────────────────
// Which list a row lives on: 'quote' = the seeded/priced takeoff grid, 'pad' =
// the tech's own ordering list. Anything not 'pad' is the grid, so an unknown
// value can never hide a row on a third, invisible list.
const laneOf = (it) => (it && it.lane === "pad" ? "pad" : "quote");
// Same private bucket + prefix the quote editor's supplier-quote drop uses —
// api/supplier-quote.js only lets the server read under supplier-quotes/.
const PAD_BUCKET = "assessment-videos";
const PAD_MAX_BYTES = 9 * 1024 * 1024;
// Web Speech where the browser has it (Chrome, Android, iOS Safari). The phone
// keyboard's own mic works in the same box regardless — this is the shortcut.
const SpeechRec = typeof window !== "undefined" ? (window.SpeechRecognition || window.webkitSpeechRecognition || null) : null;
// A drag carrying a file or text — never a row-reorder drag (that one sets no data).
const dtHasPayload = (e) => {
  const types = Array.from((e.dataTransfer && e.dataTransfer.types) || []);
  return types.includes("Files") || types.includes("text/plain");
};
// Clipboard with the execCommand fallback (older iOS Safari, non-secure
// contexts). Returns the message to show.
async function copyText(text, okMsg = "Copied ✓") {
  try { await navigator.clipboard.writeText(text); return okMsg; }
  catch {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok ? okMsg : "Copy failed — open the text view and select it by hand.";
    } catch { return "Copy failed — open the text view and select it by hand."; }
  }
}
// The whole sheet as plain text — what a tech pastes into a text, an email,
// a job note or a supplier's order form. Inputs can't be drag-selected across
// rows, so this is the copyable form. No costs: those are internal.
//   which = "all" → scope + notes + quote grid + work pad
//   which = "pad" → the ordering list alone, supplier-ready
function buildTakeoffText(sheet, items, which = "all") {
  const sd = (sheet && sheet.scope_doc) || {};
  const lineOf = (it) => {
    const part = it.part_number || null;
    const note = laneOf(it) === "pad" && String(it.notes || "").trim() ? ` (${String(it.notes).trim()})` : "";
    return `${it.qty ?? ""} ${it.unit || "ea"} — ${it.description || ""}${part ? ` [${part}]` : ""}${note}`.replace(/\s+/g, " ").trim();
  };
  const listOf = (lane) => {
    const out = [];
    for (const it of items || []) {
      if (laneOf(it) !== lane) continue;
      if (it.is_header) { if (String(it.description || "").trim()) out.push("", `## ${String(it.description).trim()}`); continue; }
      if (!String(it.description || "").trim()) continue;
      out.push(`- ${lineOf(it)}`);
    }
    return out;
  };
  const L = [];
  const jobLabel = `Job #${(sheet && sheet.job_number) || "?"}${sheet && sheet.client_name ? ` — ${sheet.client_name}` : ""}`;
  if (which === "pad") {
    L.push(`Material list — ${jobLabel}`, ...listOf("pad"));
  } else {
    L.push(`${jobLabel}${sheet && sheet.title ? ` · ${sheet.title}` : ""}`);
    const scope = Array.isArray(sd.scope) ? sd.scope : [];
    if (scope.length) { L.push("", "Scope of work"); scope.forEach(s => L.push(`- ${s}`)); }
    const notes = Array.isArray(sd.notes) ? sd.notes : [];
    if (notes.length) { L.push("", "Notes"); notes.forEach(s => L.push(`- ${s}`)); }
    const q = listOf("quote");
    if (q.length) L.push("", "Material takeoff (from the quote)", ...q);
    const p = listOf("pad");
    if (p.length) L.push("", "Work pad (ordering list)", ...p);
  }
  return L.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}
// The selection bar follows the scroll (a tech ticking work-pad lines at the
// bottom of a long sheet needs "Order these…" beside them, not at the top).
// Same 48px mobile-header offset the quote editor's pinned option bar uses.
const TK_CSS = `
.tk-selbar{position:sticky;top:48px;z-index:40;box-shadow:0 6px 16px rgba(0,0,0,.35)}
@media(min-width:768px){.tk-selbar{top:0}}
.tk-text{user-select:text;-webkit-user-select:text;cursor:text}
`;

// ═══════════════════════════════════ list view ═══════════════════════════════
function SheetList({ onOpen }) {
  const [sheets, setSheets] = useState(null);
  const [jobHits, setJobHits] = useState([]); // cache jobs matching the search that have no takeoff yet
  const [q, setQ] = useState("");
  const [err, setErr] = useState(null);
  const [creating, setCreating] = useState(false);
  const [creatingJob, setCreatingJob] = useState(null); // job_num being turned into a sheet
  const [showVanStock, setShowVanStock] = useState(false);
  const [newJob, setNewJob] = useState({ jobNumber: "", clientName: "", quoteNumber: "" });

  const load = useCallback(async (term) => {
    try {
      const r = await api(term ? "searchTakeoff" : "listSheets", { q: term || "" });
      setSheets(r.sheets); setJobHits(r.jobs || []); setErr(null);
    } catch (e) { setErr(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => load(q), 350); return () => clearTimeout(t); }, [q, load]);

  // A searched-up job with no takeoff yet → create one (seeded from its quote
  // when the quote came from ElecBid) and jump straight in.
  async function createFromJob(j) {
    setCreatingJob(j.job_num);
    try {
      const r = await api("createSheet", {
        jobNumber: j.job_num, clientName: j.client_name || null, jobberJobId: j.jobber_id || null,
        quoteNumber: j.quote_num || null, seedFromQuote: !!j.quote_num,
        title: j.title || null, source: "search",
      });
      onOpen(r.sheetId);
    } catch (e) { alert("Couldn't create: " + e.message); }
    finally { setCreatingJob(null); }
  }

  async function createSheet() {
    try {
      const r = await api("createSheet", {
        jobNumber: newJob.jobNumber || null, clientName: newJob.clientName || null,
        quoteNumber: newJob.quoteNumber || null, seedFromQuote: !!newJob.quoteNumber,
        title: newJob.clientName ? `${newJob.clientName}${newJob.jobNumber ? ` — Job #${newJob.jobNumber}` : ""}` : null,
      });
      onOpen(r.sheetId);
    } catch (e) { alert("Couldn't create: " + e.message); }
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 26, letterSpacing: ".04em", color: C.lime }}>Material Takeoff</div>
          <div style={{ fontSize: 13, color: C.muted }}>Per-job material lists — auto-created when a quote converts. Order, track, and reconcile from here.</div>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search client, job #, quote #, address, phone…"
          style={{ ...inputStyle, width: 280, border: `1px solid ${C.border}`, background: C.surface }} />
        <button style={btn()} onClick={() => setShowVanStock(true)}>Van stock</button>
        <button style={btn(true)} onClick={() => setCreating(v => !v)}>+ New takeoff</button>
      </div>
      {showVanStock && <VanStockModal tech={null} onClose={() => setShowVanStock(false)} />}

      {creating && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, marginBottom: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder="Job # (optional)" value={newJob.jobNumber} onChange={e => setNewJob(s => ({ ...s, jobNumber: e.target.value.replace(/\D/g, "") }))} style={{ ...inputStyle, width: 120, border: `1px solid ${C.border}` }} />
          <input placeholder="Client name" value={newJob.clientName} onChange={e => setNewJob(s => ({ ...s, clientName: e.target.value }))} style={{ ...inputStyle, width: 200, border: `1px solid ${C.border}` }} />
          <input placeholder="Seed from quote # (optional)" value={newJob.quoteNumber} onChange={e => setNewJob(s => ({ ...s, quoteNumber: e.target.value.replace(/\D/g, "") }))} style={{ ...inputStyle, width: 190, border: `1px solid ${C.border}` }} />
          <button style={btn(true)} onClick={createSheet}>Create</button>
          <span style={{ fontSize: 12, color: C.muted }}>Converted quotes appear here automatically — manual create is for T&M / legacy jobs.</span>
        </div>
      )}

      {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", background: "#11141a" }}>
                {["Job", "Client / Title", "Items", "To order", "Ordered/Stock", "Updated"].map((h, i) => (
                  <th key={i} style={{ padding: "9px 12px", fontWeight: 600, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!sheets && <tr><td colSpan={6} style={{ padding: 18, color: C.muted, textAlign: "center" }}>Loading…</td></tr>}
              {sheets && sheets.length === 0 && <tr><td colSpan={6} style={{ padding: 18, color: C.muted, textAlign: "center" }}>{q ? "No takeoffs match — check the jobs below." : "No takeoffs yet — they're created automatically when a quote converts to a job."}</td></tr>}
              {(sheets || []).map(s => (
                <tr key={s.id} onClick={() => onOpen(s.id)} style={{ borderBottom: "1px solid #1e2328", cursor: "pointer" }}
                  onMouseEnter={e => e.currentTarget.style.background = "rgba(165,206,58,.05)"}
                  onMouseLeave={e => e.currentTarget.style.background = "none"}>
                  <td style={{ padding: "8px 12px", color: C.lime, fontWeight: 700, whiteSpace: "nowrap" }}>{s.job_number ? `#${s.job_number}` : "—"}</td>
                  <td style={{ padding: "8px 12px", color: C.text, fontWeight: 600 }}>{s.client_name || s.title || "—"}
                    {s.client_name && s.title && s.title !== s.client_name ? <span style={{ color: C.muted, fontWeight: 400 }}> — {s.title}</span> : null}</td>
                  <td style={{ padding: "8px 12px", color: C.text }}>{s._counts.items}</td>
                  <td style={{ padding: "8px 12px" }}>{s._counts.needed ? <span style={chip(C.amber)}>{s._counts.needed}</span> : <span style={{ color: C.muted }}>0</span>}</td>
                  <td style={{ padding: "8px 12px" }}>{s._counts.ordered ? <span style={chip(C.green)}>{s._counts.ordered}</span> : <span style={{ color: C.muted }}>0</span>}</td>
                  <td style={{ padding: "8px 12px", color: C.muted, whiteSpace: "nowrap" }}>{s.updated_at ? new Date(s.updated_at).toLocaleDateString("en-CA") : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* jobs matching the search that have no takeoff yet */}
      {q && jobHits.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, marginTop: 14, padding: 14 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 18, color: C.text, marginBottom: 4 }}>Jobs without a takeoff yet</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Matched in Jobber — create a takeoff to start a material list (seeds from the ElecBid quote when there is one).</div>
          {jobHits.map(j => (
            <div key={j.job_num} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderBottom: "1px solid #1e2328", padding: "7px 0", fontSize: 13 }}>
              <span style={{ color: C.lime, fontWeight: 700, whiteSpace: "nowrap" }}>#{j.job_num}</span>
              <span style={{ color: C.text, fontWeight: 600 }}>{j.client_name || "—"}</span>
              <span style={{ color: C.muted, flex: 1, minWidth: 120 }}>{j.title || ""}</span>
              {j.quote_num ? <span style={{ color: C.muted, whiteSpace: "nowrap" }}>quote #{j.quote_num}</span> : null}
              {j.status ? <span style={chip(C.muted)}>{String(j.status).toLowerCase().replace(/_/g, " ")}</span> : null}
              <button style={btn(true)} disabled={creatingJob === j.job_num} onClick={() => createFromJob(j)}>
                {creatingJob === j.job_num ? "Creating…" : "+ Takeoff"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════ order drawer ════════════════════════════════
function OrderDrawer({ sheet, items, branches, tech, poPreview, onClose, onDone }) {
  const suppliers = useMemo(() => {
    const g = {};
    for (const b of branches) (g[b.supplier] = g[b.supplier] || []).push(b);
    return g;
  }, [branches]);
  const guess = items.find(i => i.supplier)?.supplier || "";
  const guessBranch = branches.find(b => guess && b.supplier.toLowerCase().includes(guess.toLowerCase().split(" ")[0]) && b.is_default) || null;
  const [branchId, setBranchId] = useState(guessBranch?.id || "");
  const [method, setMethod] = useState("email");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [adhoc, setAdhoc] = useState("");        // a supplier not in the directory
  const [adhocName, setAdhocName] = useState("");    // its name, if saving to the directory
  const [adhocBranch, setAdhocBranch] = useState(""); // its city/branch, optional
  const [cc, setCc] = useState("");              // crew FYI copy
  const [saveToDir, setSaveToDir] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const branch = branches.find(b => b.id === branchId) || null;
  const adhocP = parseRecipients(adhoc);
  const ccP = parseRecipients(cc);

  const preview = [
    `Material order — Browning Electric`,
    `From: ${tech.name || "?"}${sheet.job_number ? ` (Job #${sheet.job_number}${sheet.client_name ? " — " + sheet.client_name : ""})` : ""}`,
    // Mirrors orderTextMessage() on the server — keep the two in step.
    poPreview ? (poPreview === String(sheet.job_number) ? `PO / Job #: ${poPreview}` : `PO: ${poPreview}`) : null,
    ``,
    // A work-pad line's note is written for the supplier and rides along, in
    // brackets, exactly as the server's orderLines prints it.
    ...items.map(it => {
      const note = laneOf(it) === "pad" && String(it.notes || "").trim() ? String(it.notes).trim() : "";
      return `${it.qty || 1} ${it.unit || "ea"} — ${it.description}${it.part_number || it.sku ? `  [${it.part_number || it.sku}]` : ""}${note ? `  (${note})` : ""}`;
    }),
    ``,
    note || `Please confirm availability.`,
    poPreview ? `Please put PO ${poPreview} on the invoice.` : null,
  ].filter(l => l !== null).join("\n");

  // An ad-hoc address counts. This is what makes "send to a supplier we don't
  // have" work, and it also un-sticks the Send button when guessBranch failed
  // to pre-select anything — it needs the line to already carry a supplier AND
  // a first-word match AND is_default, so it misses more often than it hits.
  const canEmail = !!branch?.email || adhocP.emails.length > 0;
  const canText  = !!branch?.sms_phone || adhocP.phones.length > 0;

  // Who actually gets the order vs. who just gets told about it.
  const branchIsPrimary = method === "email" ? !!branch?.email : method === "text" ? !!branch?.sms_phone : true;
  const primaryTo = branchIsPrimary
    ? (method === "email" ? branch?.email : method === "text" ? branch?.sms_phone : (branch?.phone || "logged only — no send"))
    : (method === "email" ? adhocP.emails[0] : method === "text" ? adhocP.phones[0] : null);
  const ccEmails = [...ccP.emails, ...adhocP.emails.filter(e => e !== primaryTo)];
  const ccPhones = [...ccP.phones, ...adhocP.phones.filter(p => p !== primaryTo)];

  async function send() {
    // A free-text recipient field is a direct path to any inbox on the internet:
    // createOrder passes allowExternal:true unconditionally, opting out of
    // send-email.js's external-recipient guard. The regex, the "Sending to"
    // echo and this confirm are the only three gates. Do not remove any.
    const known = new Set(branches.map(b => (b.email || "").toLowerCase()).filter(Boolean));
    const strangers = [...adhocP.emails, ...ccP.emails]
      .filter(e => !known.has(e.toLowerCase()) && !e.toLowerCase().endsWith("@browning-electric.ca"));
    if (strangers.length && !window.confirm(
      `Send job #${sheet.job_number || "?"}${sheet.client_name ? ` (${sheet.client_name})` : ""} and the full material list to:\n\n${strangers.join("\n")}\n\n` +
      `These are not in your supplier directory.`)) return;

    setBusy(true); setResult(null);
    let dirNote = "";
    try {
      let useBranchId = branchId || null;
      // "so we don't have to go back into settings" — the second time, it IS in
      // the list. A directory-save failure never costs the order: we fall back
      // to a plain one-off send and say so.
      if (saveToDir && !branchIsPrimary && adhocName.trim() && (adhocP.emails[0] || adhocP.phones[0])) {
        try {
          const sb = await api("saveBranch", { branch: {
            supplier: adhocName.trim(), branch: adhocBranch.trim() || null,
            email: adhocP.emails[0] || null, sms_phone: adhocP.phones[0] || null,
            email_verified: false, active: true,
          } });
          if (sb.id) { useBranchId = sb.id; dirNote = ` — “${adhocName.trim()}” added to your supplier directory`; }
        } catch (e) { dirNote = ` — (couldn't save to the directory: ${e.message})`; }
      }
      const r = await api("createOrder", {
        sheetId: sheet.id, itemIds: items.map(i => i.id), branchId: useBranchId,
        supplier: branch?.supplier || (adhocName.trim() || null) || guess || null, method,
        techName: tech.name, techEmail: tech.email, deliveryNote: note || null,
        // The server already prefers body.toEmail / body.toPhone over the branch
        // row — this seam existed and had never been wired to anything.
        ...(branchIsPrimary ? {} : method === "email" ? { toEmail: primaryTo } : method === "text" ? { toPhone: primaryTo } : {}),
        ccEmails, ccPhones,
      });
      setResult({ ok: true, r, dirNote });
      onDone(r);
    } catch (e) {
      // Quo key missing / branch has no SMS → hand the tech an sms: link so
      // they can fire the same message from their own phone.
      if (method === "text" && branch?.sms_phone) {
        setResult({ ok: false, error: e.message, smsHref: `sms:${branch.sms_phone}?&body=${encodeURIComponent(preview)}` });
      } else setResult({ ok: false, error: e.message });
    } finally { setBusy(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 60, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(480px, 100vw)", height: "100%", overflowY: "auto", background: C.surface, borderLeft: `1px solid ${C.border}`, padding: 18, boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: C.lime, flex: 1 }}>Order {items.length} item{items.length > 1 ? "s" : ""}</div>
          <button style={btn()} onClick={onClose}>✕</button>
        </div>

        <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>Supplier branch</div>
        <select value={branchId} onChange={e => setBranchId(e.target.value)}
          style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card, marginBottom: 4 }}>
          <option value="">— pick a branch —</option>
          {Object.entries(suppliers).map(([sup, list]) => (
            <optgroup key={sup} label={sup}>
              {list.map(b => <option key={b.id} value={b.id}>{sup} — {b.branch}{b.email ? " ✉" : ""}{b.sms_phone ? " 📱" : ""}</option>)}
            </optgroup>
          ))}
        </select>
        {branch && (
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
            {branch.address && <div>{branch.address}</div>}
            <div>
              {branch.phone && <span>☎ {branch.phone}&nbsp;&nbsp;</span>}
              {branch.email && <span>✉ {branch.email}{branch.email_verified === false ? <span style={{ color: C.amber }}> (unverified — confirm on first use)</span> : null}</span>}
            </div>
            {branch.sms_phone && <div>📱 accepts text orders: {branch.sms_phone}</div>}
            {branch.notes && <div style={{ color: "#a3aec2" }}>{branch.notes}</div>}
          </div>
        )}

        {/* One-off recipients — no settings round-trip. */}
        <div onClick={() => setShowMore(v => !v)} style={{ fontSize: 12.5, color: C.blue, cursor: "pointer", margin: "10px 0 6px", userSelect: "none" }}>
          {showMore ? "▾" : "▸"} Send to someone else, or copy the crew
          {!showMore && (adhocP.emails.length + adhocP.phones.length + ccP.emails.length + ccP.phones.length > 0)
            ? <span style={{ color: C.lime }}> · {adhocP.emails.length + adhocP.phones.length + ccP.emails.length + ccP.phones.length} added</span> : null}
        </div>
        {showMore && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10, marginBottom: 10 }}>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>Supplier not in the list — email or cell #</div>
            <input value={adhoc} onChange={e => setAdhoc(e.target.value)} placeholder="counter@supplier.ca, 250 555 1234"
              style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.surface }} />
            <div style={{ fontSize: 11.5, marginTop: 4, minHeight: 16 }}>
              {adhocP.emails.map(e => <span key={e} style={{ ...chip(C.blue), marginRight: 4 }}>✉ {e}</span>)}
              {adhocP.phones.map(p => <span key={p} style={{ ...chip(C.purple), marginRight: 4 }}>📱 {p}</span>)}
              {adhocP.bad.length > 0 && <span style={{ color: C.red }}>Can't read: {adhocP.bad.join(", ")}</span>}
            </div>
            {!branchIsPrimary && (adhocP.emails.length > 0 || adhocP.phones.length > 0) && (
              <div style={{ marginTop: 6 }}>
                <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="checkbox" checked={saveToDir} onChange={e => setSaveToDir(e.target.checked)} />
                  also save to the supplier directory
                </label>
                {saveToDir && (
                  <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <input value={adhocName} onChange={e => setAdhocName(e.target.value)} placeholder="Supplier name (required)"
                      style={{ ...inputStyle, border: `1px solid ${adhocName.trim() ? C.border : C.amber}`, background: C.surface }} />
                    <input value={adhocBranch} onChange={e => setAdhocBranch(e.target.value)} placeholder="City (optional)"
                      style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.surface, width: 130 }} />
                  </div>
                )}
              </div>
            )}
            <div style={{ fontSize: 12, color: C.muted, margin: "10px 0 4px" }}>Also copy — tell the guys what's ordered</div>
            <input value={cc} onChange={e => setCc(e.target.value)} placeholder="kyle@browning-electric.ca, 250 555 1234"
              style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.surface }} />
            <div style={{ fontSize: 11.5, marginTop: 4, minHeight: 16 }}>
              {ccP.emails.map(e => <span key={e} style={{ ...chip(C.blue), marginRight: 4 }}>✉ {e}</span>)}
              {ccP.phones.map(p => <span key={p} style={{ ...chip(C.purple), marginRight: 4 }}>📱 {p}</span>)}
              {ccP.bad.length > 0 && <span style={{ color: C.red }}>Can't read: {ccP.bad.join(", ")}</span>}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
              Copies go out marked <b>[FYI]</b> and are never treated as an order. Texts send from the office line (Quo), not your phone — replies land in Quo.
            </div>
          </div>
        )}

        <div style={{ fontSize: 12.5, color: C.muted, margin: "8px 0 6px" }}>How</div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <button style={{ ...btn(method === "email"), opacity: canEmail || method !== "email" ? 1 : .5 }} onClick={() => setMethod("email")} disabled={!canEmail}>✉ Email{tech.email ? ` from ${tech.email.split("@")[0]}@` : ""}</button>
          <button style={btn(method === "text")} onClick={() => setMethod("text")} disabled={!canText}>📱 Text order</button>
          <button style={btn(method === "called")} onClick={() => setMethod("called")}>☎ Called it in</button>
        </div>
        {!canEmail && method === "email" && <div style={{ fontSize: 12, color: C.amber, marginBottom: 8 }}>No email for this branch — pick another branch, add a one-off address above, or use text/called.</div>}

        <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>Pickup / delivery note (optional)</div>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder={`e.g. "Pickup tomorrow 7am" or "Deliver to site — 123 Main St"`}
          style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card, marginBottom: 12 }} />

        <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>{method === "called" ? "Logged as (for the record + Jobber note)" : "Message preview"}</div>
        <pre style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, fontSize: 12, color: C.text, whiteSpace: "pre-wrap", maxHeight: 260, overflowY: "auto" }}>{preview}</pre>

        {/* The safety surface: exactly who this is about to reach, in one line. */}
        <div style={{ fontSize: 12, color: C.muted, marginTop: 10, background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: "7px 9px" }}>
          Sending to: <b style={{ color: primaryTo ? C.lime : C.amber }}>{primaryTo || "— nobody yet —"}</b>
          {(ccEmails.length + ccPhones.length) > 0 && (
            <span> · copy to <b style={{ color: C.text }}>{[...ccEmails, ...ccPhones].join(", ")}</b></span>
          )}
        </div>

        <button style={{ ...btn(true), width: "100%", padding: "11px 0", marginTop: 8, opacity: busy ? .6 : 1 }} disabled={busy || (method !== "called" && !primaryTo)} onClick={send}>
          {busy ? "Sending…" : method === "email" ? "Send order email" : method === "text" ? "Send text via Quo line" : "Log phone order"}
        </button>
        <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>
          {poPreview ? <span>PO <b style={{ color: C.lime }}>{poPreview}</b> (your job #) is stamped on the order — if the supplier echoes it on the invoice, the expense auto-links even with no job attached. </span> : null}
          The order is noted on the Jobber job and the selected rows flip to <b>Ordered</b>; the supplier invoice fills the Actual $ column when it lands.
        </div>

        {result && !result.ok && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: C.red }}>
            {result.error}
            {result.smsHref && <div style={{ marginTop: 8 }}><a href={result.smsHref} style={{ color: C.blue }}>Open in your phone's messages instead →</a></div>}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════ request for quote ════════════════════════════
// Ask SEVERAL suppliers to price the same lines so they can be compared. Every
// supplier gets the same list — that's the whole point of a quote request, and
// it's the difference from the order path, where each supplier only gets the
// lines assigned to them. Nothing is ordered and no line changes status.
function QuoteRequestDrawer({ sheet, items, branches, tech, onClose, onDone }) {
  const [picked, setPicked] = useState({});        // branchId → "email" | "text"
  const [adhoc, setAdhoc] = useState("");
  const [cc, setCc] = useState("");
  const [needBy, setNeedBy] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentResult, setSentResult] = useState(null);

  const adhocP = parseRecipients(adhoc);
  const ccP = parseRecipients(cc);
  const bySupplier = useMemo(() => {
    const g = {};
    for (const b of branches) if (b.active !== false) (g[b.supplier] = g[b.supplier] || []).push(b);
    return g;
  }, [branches]);

  const targets = [
    ...Object.entries(picked).filter(([, m]) => m).map(([branchId, method]) => {
      const b = branches.find(x => x.id === branchId);
      return { branchId, method, label: b ? `${b.supplier}${b.branch ? ` (${b.branch})` : ""}` : "supplier" };
    }),
    ...adhocP.emails.map(e => ({ method: "email", toEmail: e, label: e })),
    ...adhocP.phones.map(p => ({ method: "text", toPhone: p, label: p })),
  ];

  const preview = [
    `Request for quote — Browning Electric (not an order)`,
    `From: ${tech.name || "?"}${sheet.job_number ? ` (our job #${sheet.job_number}${sheet.client_name ? " — " + sheet.client_name : ""})` : ""}`,
    needBy ? `Need pricing by: ${needBy}` : null,
    ``,
    // A work-pad line's note is written for the supplier and rides along, in
    // brackets, exactly as the server's orderLines prints it.
    ...items.map(it => {
      const note = laneOf(it) === "pad" && String(it.notes || "").trim() ? String(it.notes).trim() : "";
      return `${it.qty || 1} ${it.unit || "ea"} — ${it.description}${it.part_number || it.sku ? `  [${it.part_number || it.sku}]` : ""}${note ? `  (${note})` : ""}`;
    }),
    ``,
    note || null,
    `Please quote unit pricing and lead time. This is a request for pricing only — we'll send a PO before anything ships.`,
  ].filter(l => l !== null).join("\n");

  const anyText = targets.some(t => t.method === "text");

  async function send() {
    const known = new Set(branches.map(b => (b.email || "").toLowerCase()).filter(Boolean));
    const strangers = [...adhocP.emails, ...ccP.emails]
      .filter(e => !known.has(e.toLowerCase()) && !e.toLowerCase().endsWith("@browning-electric.ca"));
    if (strangers.length && !window.confirm(
      `Send job #${sheet.job_number || "?"}${sheet.client_name ? ` (${sheet.client_name})` : ""} and the full material list to:\n\n${strangers.join("\n")}\n\nThese are not in your supplier directory.`)) return;

    setBusy(true);
    try {
      const r = await api("createRfq", {
        sheetId: sheet.id, itemIds: items.map(i => i.id), targets,
        ccEmails: ccP.emails, ccPhones: ccP.phones,
        needBy: needBy || null, note: note || null,
        techName: tech.name, techEmail: tech.email,
      });
      // The drawer deliberately stays OPEN and swaps to a per-target result
      // list. On a job site "did it actually go?" is the whole question.
      setSentResult(r);
    } catch (e) {
      setSentResult({ ok: false, sent: 0, failed: targets.length, results: [], error: e.message });
    } finally { setBusy(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 60, display: "flex", justifyContent: "flex-end" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(520px, 100vw)", height: "100%", overflowY: "auto", background: C.surface, borderLeft: `1px solid ${C.border}`, padding: 18, boxSizing: "border-box" }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: C.lime, flex: 1 }}>Ask for a quote — {items.length} line{items.length > 1 ? "s" : ""}</div>
          <button style={btn()} onClick={onClose}>✕</button>
        </div>

        {sentResult ? (
          <>
            <div style={{ fontSize: 13, color: sentResult.sent ? C.lime : C.red, marginBottom: 10, fontWeight: 700 }}>
              {sentResult.sent || 0} sent{sentResult.failed ? ` · ${sentResult.failed} failed` : ""}
            </div>
            {sentResult.error && <div style={{ fontSize: 12.5, color: C.red, marginBottom: 10 }}>{sentResult.error}</div>}
            {(sentResult.results || []).map((r, i) => (
              <div key={i} style={{ borderBottom: "1px solid #1e2328", padding: "8px 0", fontSize: 12.5 }}>
                <span style={{ color: r.ok ? C.green : C.red, fontWeight: 700 }}>{r.ok ? "✓" : "✗"}</span>{" "}
                <b style={{ color: C.text }}>{r.label}</b>{" "}
                <span style={{ color: C.muted }}>{r.method === "text" ? "📱" : "✉"} {r.recipient || "—"}</span>
                {!r.ok && <div style={{ color: C.red, marginTop: 3 }}>{r.error}</div>}
                {r.smsFallback && (
                  <div style={{ marginTop: 4 }}>
                    <a href={`sms:${r.smsFallback.to}?&body=${encodeURIComponent(r.smsFallback.body)}`} style={{ color: C.blue }}>Send it from your own phone →</a>
                  </div>
                )}
              </div>
            ))}
            {(sentResult.copies || []).length > 0 && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 10 }}>
                Copies: {sentResult.copies.map(c => `${c.to}${c.ok ? "" : " (failed)"}`).join(", ")}
              </div>
            )}
            <button style={{ ...btn(true), width: "100%", padding: "11px 0", marginTop: 14 }} onClick={onDone}>Done</button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
              Every supplier you tick gets the <b>same</b> list, so you can compare prices. Nothing is ordered and no line changes status.
            </div>

            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>Who to ask</div>
            <div style={{ maxHeight: 240, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 10, padding: 8, marginBottom: 10 }}>
              {Object.keys(bySupplier).length === 0 && <div style={{ fontSize: 12, color: C.amber }}>No suppliers loaded — reload the page if your session expired.</div>}
              {Object.entries(bySupplier).map(([sup, list]) => (
                <div key={sup} style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 11.5, color: C.lime, fontWeight: 700, marginBottom: 2 }}>{sup}</div>
                  {list.map(b => {
                    const reachable = !!b.email || !!b.sms_phone;
                    const m = picked[b.id] || "";
                    return (
                      <div key={b.id} style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12, padding: "3px 0", opacity: reachable ? 1 : .5 }}>
                        <input type="checkbox" disabled={!reachable} checked={!!m}
                          onChange={e => setPicked(p => ({ ...p, [b.id]: e.target.checked ? (b.email ? "email" : "text") : "" }))} />
                        <span style={{ color: C.text, minWidth: 90 }}>{b.branch || "—"}</span>
                        <span style={{ color: C.muted, flex: 1 }}>
                          {reachable
                            ? (m === "text" ? `📱 ${b.sms_phone}` : b.email ? `✉ ${b.email}` : `📱 ${b.sms_phone}`)
                            : "no email or SMS — add one below, or in Suppliers ✎"}
                        </span>
                        {reachable && b.email && b.sms_phone && m && (
                          <span onClick={() => setPicked(p => ({ ...p, [b.id]: m === "email" ? "text" : "email" }))}
                            style={{ ...chip(C.blue), cursor: "pointer" }}>{m === "email" ? "✉" : "📱"}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 4 }}>Someone not in the list (email or cell #)</div>
            <input value={adhoc} onChange={e => setAdhoc(e.target.value)} placeholder="quotes@supplier.ca, 250 555 1234"
              style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card }} />
            {adhocP.bad.length > 0 && <div style={{ fontSize: 11.5, color: C.red, marginTop: 3 }}>Can't read: {adhocP.bad.join(", ")}</div>}

            <div style={{ display: "flex", gap: 8, margin: "10px 0" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 4 }}>Need pricing by</div>
                <input type="date" value={needBy} onChange={e => setNeedBy(e.target.value)}
                  style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card }} />
              </div>
            </div>

            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 4 }}>Note to suppliers (optional)</div>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. pricing + lead time please, job starts the 20th"
              style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card, marginBottom: 10 }} />

            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 4 }}>Also copy (crew FYI)</div>
            <input value={cc} onChange={e => setCc(e.target.value)} placeholder="kyle@browning-electric.ca"
              style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card, marginBottom: 10 }} />

            <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 6 }}>Message preview</div>
            <pre style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, fontSize: 12, color: C.text, whiteSpace: "pre-wrap", maxHeight: 220, overflowY: "auto" }}>{preview}</pre>
            {anyText && preview.length > 1400 && (
              <div style={{ fontSize: 12, color: C.amber, marginTop: 6 }}>⚠ This is {preview.length} characters — texts are cut off around 1500. Email the long ones.</div>
            )}

            <button style={{ ...btn(true), width: "100%", padding: "11px 0", marginTop: 10, opacity: busy || !targets.length ? .6 : 1 }}
              disabled={busy || !targets.length} onClick={send}>
              {busy ? "Sending…" : targets.length ? `Send price request to ${targets.length}` : "Pick at least one supplier"}
            </button>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>
              No PO is stamped and nothing flips to <b>Ordered</b> — this is a question, not a commitment. The request is noted on the Jobber job.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════ supplier directory ═══════════════════════════
function BranchDirectory({ branches, onClose, onSaved }) {
  const [edit, setEdit] = useState(null); // branch row being edited (or {new:true})
  const [busy, setBusy] = useState(false);
  const f = (k, ph, w = 170) => (
    <input placeholder={ph} value={edit?.[k] || ""} onChange={e => setEdit(s => ({ ...s, [k]: e.target.value }))}
      style={{ ...inputStyle, width: w, border: `1px solid ${C.border}`, background: C.card }} />
  );
  async function save() {
    setBusy(true);
    try { await api("saveBranch", { branch: { ...edit, email_verified: !!edit.email_verified } }); setEdit(null); onSaved(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  }
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(760px, 100%)", maxHeight: "85vh", overflowY: "auto", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: C.lime, flex: 1 }}>Supplier directory</div>
          <button style={btn()} onClick={() => setEdit({ new: true, supplier: "", branch: "" })}>+ Branch</button>
          <button style={{ ...btn(), marginLeft: 8 }} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>Branch emails/phones used by the order flow. ⚠ = email found online but not yet confirmed with the branch.</div>
        {edit && (
          <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, marginBottom: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {f("supplier", "Supplier (e.g. City Electric)", 170)}{f("branch", "Branch/City", 120)}{f("email", "Order email", 210)}
            {f("phone", "Phone", 120)}{f("sms_phone", "SMS # (+1…)", 120)}{f("address", "Address", 240)}{f("notes", "Notes", 240)}
            <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 5 }}>
              <input type="checkbox" checked={!!edit.email_verified} onChange={e => setEdit(s => ({ ...s, email_verified: e.target.checked }))} /> email confirmed
            </label>
            <button style={btn(true)} disabled={busy} onClick={save}>{busy ? "…" : "Save"}</button>
            <button style={btn()} onClick={() => setEdit(null)}>Cancel</button>
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead><tr style={{ color: C.muted, textAlign: "left" }}>{["Supplier", "Branch", "Email", "Phone", "SMS", ""].map((h, i) => <th key={i} style={{ padding: "6px 8px", borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
          <tbody>
            {branches.map(b => (
              <tr key={b.id} style={{ borderBottom: "1px solid #1e2328" }}>
                <td style={{ padding: "6px 8px", color: C.text, fontWeight: 600 }}>{b.supplier}</td>
                <td style={{ padding: "6px 8px", color: C.text }}>{b.branch}</td>
                <td style={{ padding: "6px 8px", color: C.muted }}>{b.email || "—"}{b.email && b.email_verified === false ? " ⚠" : ""}</td>
                <td style={{ padding: "6px 8px", color: C.muted, whiteSpace: "nowrap" }}>{b.phone || "—"}</td>
                <td style={{ padding: "6px 8px", color: C.muted, whiteSpace: "nowrap" }}>{b.sms_phone ? "📱 " + b.sms_phone : "—"}</td>
                <td style={{ padding: "6px 8px" }}><button style={btn()} onClick={() => setEdit(b)}>Edit</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════ pricebook search + save (from Quote Builder) ═════════════
// The book wants CATALOGUE descriptions, not site wording — same deterministic
// strip the quote builder uses (SavePricePanel prefills with it, tech can edit).

// Throws on a failed request instead of returning [] — an expired session
// (401) rendering as "no results" is indistinguishable from an empty price
// book, so callers surface the message rather than a silent empty list.
async function pricebookSearch(term, limit = 12) {
  const r = await apiFetch("/api/pricebook", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ action: "quoteSearch", term: String(term || "").trim(), limit }),
  });
  if (r.status === 401 || r.status === 403) {
    throw new Error("Your session expired — refresh the page to sign back in.");
  }
  const j = await r.json();
  if (!r.ok || j.ok === false) throw new Error(j.error || `Price book search failed (${r.status}).`);
  return (Array.isArray(j.results) ? j.results : [])
    .flatMap(p => (p.prices || []).map(pr => ({ p, pr })))
    .sort((a, b) => new Date(b.pr.date_recorded || 0) - new Date(a.pr.date_recorded || 0))
    .slice(0, limit);
}

// 💾 — push a verified price back to the book (same endpoint as the quote
// builder: pricebook action upsertQuotePrice; creates the product if new).
function SavePricePanel({ item, suppliers, onSaved, onClose }) {
  const [supplier, setSupplier] = useState(item.supplier || "City Electric");
  const [customSupplier, setCustomSupplier] = useState("");
  const [saveDesc, setSaveDesc] = useState(() => cleanCatalogDescription(item.description));
  const wasCleaned = saveDesc !== String(item.description || "").trim();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // The server rejects a description that still reads as a quote line, and
  // hands back a cleaned suggestion — offer it as one click rather than making
  // the tech retype it mid-job.
  const [suggestion, setSuggestion] = useState(null);
  const chosen = supplier === "__custom__" ? customSupplier.trim() : supplier;
  const cost = Number(item.unit_cost) || 0;

  async function save() {
    if (!chosen || !cost) { setErr(!cost ? "Set the cost first" : "Pick a supplier"); return; }
    if (!saveDesc.trim()) { setErr("Description can't be empty"); return; }
    setBusy(true); setErr(null); setSuggestion(null);
    try {
      const r = await apiFetch("/api/pricebook", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "upsertQuotePrice",
          master_product_id: item.master_product_id || null,
          description: saveDesc.trim(), supplier_name: chosen,
          unit_price: cost, unit: item.unit || "ea",
        }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) {
        if (j.suggestion) setSuggestion(j.suggestion);
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      onSaved(j);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
      <span style={{ color: C.muted }}>Save <b style={{ color: C.text }}>{money(cost)}</b> to the book as</span>
      <input style={{ ...inputStyle, flex: 1, minWidth: 220, border: `1px solid ${C.border}` }} value={saveDesc} onChange={e => setSaveDesc(e.target.value)}
        title="Catalogue description saved to the price book — keep it site-neutral so future quotes match it" />
      {wasCleaned && <span style={{ color: C.amber, fontSize: 10.5, flexBasis: "100%" }}>Site wording stripped for the book — edit above if it cut too much. The takeoff line keeps your original text.</span>}
      <span style={{ color: C.muted }}>from</span>
      <select value={supplier} onChange={e => setSupplier(e.target.value)} style={{ ...inputStyle, width: 170, border: `1px solid ${C.border}`, background: C.card }}>
        {(suppliers?.length ? suppliers : ["City Electric", "EB Horsman", "Wesco"]).map(s => <option key={s} value={s}>{s}</option>)}
        <option value="__custom__">Other…</option>
      </select>
      {supplier === "__custom__" && <input style={{ ...inputStyle, width: 150, border: `1px solid ${C.border}` }} placeholder="Supplier name" value={customSupplier} onChange={e => setCustomSupplier(e.target.value)} />}
      <button onClick={save} style={btn(true)} disabled={busy}>{busy ? "Saving…" : "Save to price book"}</button>
      <button onClick={onClose} style={btn()} disabled={busy}>Cancel</button>
      {err && (
        <span style={{ color: C.red, flexBasis: "100%" }}>
          {err}
          {suggestion && (
            <>
              {" "}
              <button
                style={{ ...btn(), padding: "2px 8px", fontSize: 11.5 }}
                onClick={() => { setSaveDesc(suggestion); setErr(null); setSuggestion(null); }}
              >
                Use “{suggestion}”
              </button>
            </>
          )}
        </span>
      )}
    </div>
  );
}

// 🔍 — pricebook keyword search modal (same data as the Price List tab).
function PricebookSearchModal({ initialTerm, onPick, onClose }) {
  const [term, setTerm] = useState(initialTerm || "");
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  useEffect(() => {
    const t = String(term || "").trim();
    if (t.length < 3) { setResults(null); setSearchError(""); return; }
    const h = setTimeout(async () => {
      setBusy(true);
      try { setResults(await pricebookSearch(t, 20)); setSearchError(""); }
      catch (err) { setSearchError(err.message || "Price book search failed."); setResults(null); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [term]);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 70, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 16px 16px" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(620px, 100%)", maxHeight: "72vh", overflowY: "auto", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14 }}>
        <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
          <input autoFocus value={term} onChange={e => setTerm(e.target.value)} placeholder="Search the price book…"
            style={{ ...inputStyle, flex: 1, border: `1px solid ${C.border}`, background: C.card }} />
          <button style={btn()} onClick={onClose}>✕</button>
        </div>
        {busy && <div style={{ color: C.muted, fontSize: 12, padding: 8 }}>Searching…</div>}
        {!busy && searchError && (
          <div style={{ color: "#fca5a5", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 8, fontSize: 12, padding: 10 }}>{searchError}</div>
        )}
        {!busy && !searchError && results?.length === 0 && <div style={{ color: C.muted, fontSize: 12, padding: 8 }}>No match — set your cost on the line, then 💾 to add it to the book.</div>}
        {!busy && (results || []).map(({ p, pr }, k) => (
          <button key={k} onClick={() => onPick(p, pr)}
            style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: `1px solid ${C.border}`, background: "transparent", color: C.text, padding: "8px 10px", fontSize: 12.5, fontFamily: "inherit", cursor: "pointer" }}>
            <span style={{ fontWeight: 700, color: C.lime }}>{money(pr.unit_price)}</span>
            <span> {pr.supplier_name}</span>
            <span style={{ color: C.muted, fontSize: 11 }}> · {(pr.date_recorded || "").slice(0, 10)}</span>
            <div style={{ fontSize: 11.5, color: C.muted }}>
              {pr.display_description || pr.supplier_description || p.description}{p.sku ? ` · ${p.sku}` : ""}
              {p.times_purchased > 0 && (
                <span style={{ marginLeft: 6, color: C.lime, fontWeight: 700 }} title="How often we've bought this">· bought {p.times_purchased}×</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════ van / sea-can stock ══════════════════════════
const STOCK_OWNERS = ["Sea can", "Shop", "Kyle's van", "Tommy's van", "Justin's van", "Morgan's van", "Landon's van", "Colton's van", "Pippin's van"];
function VanStockModal({ tech, onClose }) {
  const [rows, setRows] = useState(null);
  const [deleted, setDeleted] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const load = useCallback(async () => {
    try { setRows((await api("vanStock")).rows.map(r => ({ ...r }))); setDeleted([]); setDirty(false); }
    catch (e) { alert(e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (id, patch) => { setRows(prev => prev.map(r => (r === id || r.id === id ? { ...r, ...patch } : r))); setDirty(true); };
  const addRow = () => { setRows(prev => [{ _new: Date.now(), owner: STOCK_OWNERS[0], description: "", qty: 1, unit: "ea", updated_by: tech?.name || null }, ...(prev || [])]); setDirty(true); };
  const del = (r) => { setRows(prev => prev.filter(x => x !== r)); if (r.id) setDeleted(d => [...d, r.id]); setDirty(true); };
  const save = async () => {
    setBusy(true);
    try { await api("saveVanStock", { upserts: rows.filter(r => String(r.description || "").trim()), deletes: deleted }); await load(); }
    catch (e) { alert(e.message); } finally { setBusy(false); }
  };
  const shown = (rows || []).filter(r => !filter.trim()
    || `${r.owner} ${r.description} ${r.part_number || ""}`.toLowerCase().includes(filter.trim().toLowerCase()));

  const cell = { padding: "3px 4px" };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(860px, 100%)", maxHeight: "88vh", overflowY: "auto", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: C.lime, flex: 1 }}>Van / sea-can stock</div>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter…" style={{ ...inputStyle, width: 160, border: `1px solid ${C.border}`, background: C.card }} />
          <button style={btn()} onClick={addRow}>+ Item</button>
          {dirty && <button style={btn(true)} disabled={busy} onClick={save}>{busy ? "Saving…" : "Save"}</button>}
          <button style={btn()} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
          What's already on hand, by location. "Check stock" on a takeoff searches this list, and using an item counts it down. Counts are advisory — the guys keep them honest.
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, minWidth: 640 }}>
            <thead><tr style={{ color: C.muted, textAlign: "left" }}>{["Location", "Description", "Part #", "Qty", "Unit", "Notes", ""].map((h, i) => <th key={i} style={{ padding: "6px 6px", borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
            <tbody>
              {!rows && <tr><td colSpan={7} style={{ padding: 14, color: C.muted, textAlign: "center" }}>Loading…</td></tr>}
              {rows && shown.length === 0 && <tr><td colSpan={7} style={{ padding: 14, color: C.muted, textAlign: "center" }}>{filter ? "No matches." : "Empty — add what's in the vans, the sea can, and the shop."}</td></tr>}
              {shown.map((r, i) => (
                <tr key={r.id || r._new || i} style={{ borderBottom: "1px solid #1e2328" }}>
                  <td style={{ ...cell, minWidth: 130 }}><input list="stock-owners" value={r.owner || ""} onChange={e => set(r, { owner: e.target.value })} style={inputStyle} /></td>
                  <td style={{ ...cell, minWidth: 240 }}><input value={r.description || ""} onChange={e => set(r, { description: e.target.value })} style={inputStyle} /></td>
                  <td style={{ ...cell, minWidth: 90 }}><input value={r.part_number || ""} onChange={e => set(r, { part_number: e.target.value })} style={inputStyle} /></td>
                  <td style={{ ...cell, width: 60 }}><input inputMode="decimal" key={`q_${r.id || r._new || i}_${r.qty ?? ""}`} defaultValue={r.qty ?? ""}
                    onBlur={e => { const raw = e.target.value.trim().replace(",", "."); const n = raw === "" ? null : Number(raw); if (raw !== "" && !isFinite(n)) { e.target.value = r.qty ?? ""; return; } if ((n ?? null) !== (r.qty ?? null)) set(r, { qty: n }); }}
                    style={{ ...inputStyle, textAlign: "right" }} /></td>
                  <td style={{ ...cell, width: 55 }}><input value={r.unit || ""} onChange={e => set(r, { unit: e.target.value })} style={inputStyle} /></td>
                  <td style={{ ...cell, minWidth: 120 }}><input value={r.notes || ""} onChange={e => set(r, { notes: e.target.value })} style={inputStyle} /></td>
                  <td style={cell}><span onClick={() => del(r)} title="Remove" style={{ cursor: "pointer", color: C.muted }}>✕</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <datalist id="stock-owners">{STOCK_OWNERS.map(o => <option key={o} value={o} />)}</datalist>
      </div>
    </div>
  );
}

// ═══════════════════════════════ sheet detail ════════════════════════════════
function SheetDetail({ sheetId, onBack, perms }) {
  const [data, setData] = useState(null);       // { sheet, items, orders }
  const [items, setItems] = useState([]);       // local editable copy
  // Pending deletes live in a REF, not state: a per-render state snapshot got
  // cleared by an in-flight save's setDeleted([]) while a newer delete was
  // pending — the delete silently never sent and the row resurrected on reload.
  const deletedRef = useRef([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);   // Date of the last successful write
  const [err, setErr] = useState(null);
  const [selected, setSelected] = useState(new Set());
  // Which taxes the displayed cost figures include. Ex tax by default so the
  // numbers sit on the same basis a supplier quotes (and the same basis the
  // Actual $ column is on). Shared with the quote's materials table.
  const [taxBasis, setTaxBasis] = useState(() => {
    try { const j = JSON.parse(localStorage.getItem(MAT_TAX_KEY) || "{}"); return { pst: !!j.pst, gst: !!j.gst }; }
    catch { return { pst: false, gst: false }; }
  });
  const changeTaxBasis = (patch) => setTaxBasis(prev => {
    const next = { ...prev, ...patch };
    try { localStorage.setItem(MAT_TAX_KEY, JSON.stringify(next)); } catch { /* private window */ }
    return next;
  });
  const [suppliers, setSuppliers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [repriceSupplier, setRepriceSupplier] = useState("");
  const [repriceMsg, setRepriceMsg] = useState(null);
  const [repriceBusy, setRepriceBusy] = useState(false);
  const [ordering, setOrdering] = useState(null);   // null | "order" | "rfq"
  const [branchesError, setBranchesError] = useState("");
  const [showDirectory, setShowDirectory] = useState(false);
  const [showVanStock, setShowVanStock] = useState(false);
  const [pkgSave, setPkgSave] = useState(null);  // { defaultName, items } — Save-as-package modal
  const [pkgAdd, setPkgAdd] = useState(false);   // Add-package picker open
  const [msg, setMsg] = useState(null);
  const [stockMatches, setStockMatches] = useState({});   // itemId → van_stock matches
  const [reviewOpen, setReviewOpen] = useState(null);     // itemId with the $? panel open
  const [unlinked, setUnlinked] = useState([]);           // job invoices not linked to an order
  const [linkSel, setLinkSel] = useState({});             // invoiceId → orderId chosen in the link picker
  // Quote-builder-style pricebook UX on the grid
  const [inline, setInline] = useState(null);             // { idx, term } — typeahead on the description cell
  const [inlineResults, setInlineResults] = useState(null);
  const [inlineBusy, setInlineBusy] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const [searchIdx, setSearchIdx] = useState(null);       // row with the 🔍 modal open
  const [savePriceIdx, setSavePriceIdx] = useState(null); // row with the 💾 panel open
  // ── filter / search / bulk / colour ────────────────────────────────────────
  const [q, setQ] = useState("");                     // raw search box
  const [qTokens, setQTokens] = useState([]);         // debounced, normalized tokens
  const [fStatus, setFStatus] = useState("");         // "" = all
  const [fSupplier, setFSupplier] = useState("");     // "" = all, NO_SUPPLIER = blank
  const [fColor, setFColor] = useState("");           // "" = all, a hex, or ANY_COLOR
  const [stickyKey, setStickyKey] = useState(null);   // row pinned visible while focused / just added
  const [hlOpen, setHlOpen] = useState(null);         // rowKey whose swatch popover is open
  const [locAsk, setLocAsk] = useState(null);         // { key, idx } — "where is it?" prompt
  // Default ON: colour-by-status is how you see what's still outstanding at a
  // glance, so it has to be the out-of-the-box behaviour, not an opt-in.
  const [tintByStatus, setTintByStatus] = useState(() => {
    try { return localStorage.getItem("takeoff_tint") !== "0"; } catch { return true; }
  });
  const [pendingBulk, setPendingBulk] = useState(null); // { scope, field, value, label, count }
  const [bulkUndo, setBulkUndo] = useState(null);       // { label, field, groups:[{value, ids}] }
  // Jobber context (photos + human notes) so the tech plans here, not in Jobber
  const [jobberCtx, setJobberCtx] = useState(null);       // { media, notes }
  const [showJobberNotes, setShowJobberNotes] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(true);
  // Takeoff assistant chat
  const [chatMsgs, setChatMsgs] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const chatEndRef = useRef(null);
  // ── work pad + text view ──
  const [textView, setTextView] = useState(false);       // selectable plain-text rendering of the whole sheet
  const [padOver, setPadOver] = useState(false);         // a file / text is being dragged over the pad
  const [padBusy, setPadBusy] = useState(null);          // import progress text
  const [padPasteOpen, setPadPasteOpen] = useState(false);
  const [padPaste, setPadPaste] = useState("");
  const [padMore, setPadMore] = useState(false);         // show supplier / cost / location / purpose on the pad
  const [padChatInput, setPadChatInput] = useState("");
  const [padChatBusy, setPadChatBusy] = useState(false);
  const [padUndo, setPadUndo] = useState(null);          // { ids, n } — the last import, removable in one tap
  const [pendingPad, setPendingPad] = useState(null);    // { keys, count, carry } — staged "send down to the pad"
  const [dictating, setDictating] = useState(null);      // "top" | "pad" while the mic is listening
  const dictRef = useRef(null);
  const padChatEndRef = useRef(null);
  const padSelfDragRef = useRef(false);                  // a drag that STARTED inside the pad card (text from its own inputs)
  // Spreadsheet-style block selection over both tables: press a cell, drag
  // across and down, Copy / Ctrl+C. The copy handler is assigned further down
  // (it needs the column helpers), so the hook reaches it through a ref.
  const copyRangeRef = useRef(() => {});
  const { range: cellRange, rc, clearRange, copied: rangeCopied, markCopied } = useCellRange({ id: "takeoff", onCopy: (r) => copyRangeRef.current(r) });
  const dragFromRef = useRef(null);
  const saveTimer = useRef(null);
  const pendingRef = useRef(null);        // latest {items} snapshot awaiting the debounced save
  const dirtyKeysRef = useRef(new Set()); // row keys (id or _ck) edited since the last successful save
  const flushChainRef = useRef(Promise.resolve());
  const flushRef = useRef(() => Promise.resolve()); // load() runs before flushSave is defined — call through this
  const itemsRef = useRef([]);

  const tech = {
    name: perms.jobberEmployeeName || perms.crewMember?.name || (perms.crewMember?.email || "").split("@")[0] || null,
    email: perms.crewMember?.email || null,
  };

  const load = useCallback(async () => {
    try {
      // Flush (not drop) any pending edits first — a debounced save firing
      // AFTER this refetch would push pre-reload rows back over fresh data.
      await flushRef.current();
      const r = await api("getSheet", { sheetId });
      setData(r); setItems(r.items.map(x => ({ ...x }))); deletedRef.current = []; setDirty(false);
      // Seed the indicator from the newest row so a freshly opened sheet shows
      // when it was really last touched, rather than a blank until the first edit.
      const newest = (r.items || []).reduce((a, x) => (x.updated_at && x.updated_at > a ? x.updated_at : a), "");
      setLastSavedAt(newest ? new Date(newest) : null);
      setChatMsgs(Array.isArray(r.sheet?.chat_log) ? r.sheet.chat_log : []);
      api("unlinkedExpenses", { sheetId }).then(u => setUnlinked(u.invoices || [])).catch(() => {});
      if (r.sheet?.jobber_job_id) api("jobMedia", { sheetId }).then(m => setJobberCtx({ media: m.media || [], notes: m.notes || [] })).catch(() => {});
    } catch (e) { setErr(e.message); }
  }, [sheetId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { itemsRef.current = items; }, [items]);
  // A PDF dropped a few pixels outside the work pad would otherwise make the
  // browser open the file in place of the page — and take the sheet with it.
  useEffect(() => {
    const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes("Files");
    const guard = (e) => { if (hasFiles(e) && !e.defaultPrevented) e.preventDefault(); };
    window.addEventListener("dragover", guard);
    window.addEventListener("drop", guard);
    return () => { window.removeEventListener("dragover", guard); window.removeEventListener("drop", guard); };
  }, []);
  // Stop the mic when the sheet unmounts mid-dictation.
  useEffect(() => () => { try { dictRef.current?.stop(); } catch { /* already stopped */ } }, []);
  useEffect(() => {
    // Surfaced, not swallowed: an expired session used to leave `branches`
    // empty, so the supplier pickers looked mysteriously blank with no reason.
    api("listSuppliers")
      .then(r => { setSuppliers(r.suppliers || []); setBranches(r.branches || []); setBranchesError(""); })
      .catch(e => setBranchesError(e.message || "Couldn't load the supplier list."));
  }, []);
  // The sheet's chosen supplier drives the reprice default too.
  useEffect(() => { if (data?.sheet?.preferred_supplier) setRepriceSupplier(p => p || data.sheet.preferred_supplier); }, [data?.sheet?.preferred_supplier]);
  // Search debounce. No minimum length and no 3-char guard — this filters rows
  // already in memory, so there is no request to throttle (unlike the pricebook
  // typeaheads below, where the guard exists to protect a network call).
  useEffect(() => {
    const t = setTimeout(() => setQTokens(normText(q).split(" ").filter(Boolean)), 180);
    return () => clearTimeout(t);
  }, [q]);
  // Inline pricebook typeahead (same behaviour as the quote builder's editor).
  useEffect(() => {
    if (!inline || String(inline.term || "").trim().length < 3) { setInlineResults(null); return; }
    const t = setTimeout(async () => {
      setInlineBusy(true);
      try { setInlineResults(await pricebookSearch(inline.term, 10)); setInlineError(""); }
      catch (err) { setInlineError(err.message || "Price book search failed."); setInlineResults(null); }
      finally { setInlineBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [inline]);

  const sheet = data?.sheet;
  // The grid's builtin columns are hardcoded (quote-builder layout); only the
  // tech-added custom columns come from the stored config.
  const customCols = useMemo(() => (Array.isArray(sheet?.columns) ? sheet.columns : []).filter(c => c.custom), [sheet]);

  // ── save (explicit + debounced auto) ───────────────────────────────────────
  // Only DIRTY rows are sent (plus id-less new rows). The old full-sheet
  // rewrite resurrected stale values over anything written server-side after
  // this tab loaded (expense actuals, bulk status, use-stock, review accepts).
  const doSave = useCallback(async (rows, dels) => {
    setSaving(true);
    try {
      const r = await api("saveItems", { sheetId, upserts: rows, deletes: dels });
      // Adopt server ids for new rows — by the echoed _ck first (exact), then
      // by position + normalized description ("" vs null must compare equal or
      // a not-yet-described row re-inserts itself on every autosave).
      if (r.items?.length) {
        setItems(prev => {
          const next = prev.map(it => {
            if (it.id) return it;
            const hit = (it._ck && r.items.find(s => s._ck === it._ck))
              || r.items.find(s => Number(s.position) === Number(it.position) && String(s.description || "") === String(it.description || ""));
            return hit ? { ...it, id: hit.id } : it;
          });
          // A row inserted by this save but DELETED from the grid mid-flight
          // would otherwise resurrect on reload — queue its fresh id for delete.
          let orphans = false;
          for (const s of r.items) {
            if (s._ck && !prev.some(it => it._ck === s._ck)) { deletedRef.current.push(s.id); orphans = true; }
          }
          if (orphans) setTimeout(() => flushRef.current(), 50);
          itemsRef.current = next; // callers read fresh ids right after a flush
          return next;
        });
      }
      // Only clear the deletes this save actually sent — newer ones stay queued.
      deletedRef.current = deletedRef.current.filter(id => !dels.includes(id));
      setDirty(false);
      setLastSavedAt(new Date());
      // Clear a stale failure banner. Without this, a save that has since
      // SUCCEEDED still shows the old red "Save failed" line until a reload —
      // which is exactly how a working save reads as a broken one.
      setErr(null);
    } catch (e) { setErr("Save failed: " + e.message); throw e; }
    finally { setSaving(false); }
  }, [sheetId]);

  // Flush the pending debounced save NOW, serialized so a second flush queues
  // behind an in-flight one. Called before every direct server mutation (bulk
  // status, use-stock, review accept, ordering, reprice, reload) — a stale
  // timer firing after those wrote to the DB used to revert them.
  const flushSave = useCallback(() => {
    const run = async () => {
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
      const p = pendingRef.current;
      const dels = deletedRef.current.slice();
      if (!p && !dels.length) return;
      pendingRef.current = null;
      const keys = new Set(dirtyKeysRef.current);
      dirtyKeysRef.current = new Set();
      const rows = (p ? p.items : itemsRef.current).filter((it, i) => !it.id || keys.has(rowKey(it, i)));
      try {
        if (rows.length || dels.length) await doSave(rows, dels);
        if (pendingRef.current) setDirty(true); // edits landed mid-save — still unsaved
      } catch {
        // keep the edits marked dirty so the next change retries them
        for (const k of keys) dirtyKeysRef.current.add(k);
        if (!pendingRef.current) pendingRef.current = p;
      }
    };
    flushChainRef.current = flushChainRef.current.then(run, run);
    return flushChainRef.current;
  }, [doSave]);
  flushRef.current = flushSave;

  const scheduleSave = useCallback((nextItems) => {
    setDirty(true);
    pendingRef.current = { items: nextItems };
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => { saveTimer.current = null; flushSave(); }, 1600);
  }, [flushSave]);

  function setItem(idx, patch) {
    setItems(prev => {
      const next = prev.slice();
      next[idx] = { ...next[idx], ...patch };
      dirtyKeysRef.current.add(rowKey(next[idx], idx));
      scheduleSave(next);
      return next;
    });
  }
  function setCustom(idx, key, value) {
    setItems(prev => {
      const next = prev.slice();
      next[idx] = { ...next[idx], custom: { ...(next[idx].custom || {}), [key]: value } };
      dirtyKeysRef.current.add(rowKey(next[idx], idx));
      scheduleSave(next);
      return next;
    });
  }
  function addRow(isHeader = false, atIdx = null, lane = "quote") {
    // Minted out here so the new row can be pinned visible after the update — a
    // fresh row is `needed` with no supplier and would instantly fail an active
    // filter, vanishing the moment the tech created it.
    const ck = "ck_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    setItems(prev => {
      const i = atIdx == null ? prev.length : atIdx + 1;
      const pos = i < prev.length
        ? ((Number(prev[Math.max(0, i - 1)]?.position) || 0) + (Number(prev[i]?.position) || (Number(prev[Math.max(0, i - 1)]?.position) || 0) + 20)) / 2
        : (Number(prev[prev.length - 1]?.position) || 0) + 10;
      // same defaults as the quote builder's addRow (markup 35, PST 7).
      // _ck = stable client key so selection/dirty-tracking survive id adoption
      // (stripped server-side — cleanItem only copies ITEM_FIELDS).
      // A work-pad row carries NO pricing knobs: the pad is qty + description,
      // and a null markup/PST is what keeps it out of the grid's money totals.
      const pad = lane === "pad";
      const row = { _ck: ck, sheet_id: sheetId, lane, position: pos, is_header: isHeader, qty: isHeader ? null : 1, unit: isHeader ? null : "ea", description: "", status: "needed", markup_pct: isHeader || pad ? null : 35, pst_pct: isHeader || pad ? null : 7, source: "manual", created_by: tech.name, custom: {} };
      dirtyKeysRef.current.add(row._ck);
      const next = [...prev.slice(0, i), row, ...prev.slice(i)];
      scheduleSave(next);
      return next;
    });
    setStickyKey(ck);
  }
  function removeRow(idx) {
    setItems(prev => {
      const gone = prev[idx];
      const next = prev.filter((_, i) => i !== idx);
      if (gone?.id) deletedRef.current.push(gone.id);
      scheduleSave(next);
      return next;
    });
  }
  // Delete is the one bulk op that destroys data — it goes through the same
  // staged confirm as the rest. `selected` is pruned to visible rows by the
  // filter invariant, so this can never remove a line the tech cannot see.
  function doRemoveKeys(keys) {
    const ids = items.filter((it, i) => keys.has(rowKey(it, i)) && it.id).map(it => it.id);
    setItems(prev => {
      const next = prev.filter((it, i) => !keys.has(rowKey(it, i)));
      deletedRef.current.push(...ids);
      scheduleSave(next);
      return next;
    });
    setSelected(new Set());
    setMsg(`Deleted ${keys.size} line(s).`);
  }
  function removeSelected() {
    if (!selItems.length) return;
    setPendingBulk({ scope: "selection", field: "__delete__", value: null, label: "delete", count: selItems.length });
  }
  // ⠿ drag-to-reorder (same interaction as the quote builder) — drop re-slots
  // the row and positions renumber 10,20,30… so ordering stays stable.
  function moveRow(from, to) {
    if (from == null || to == null || from === to) return;
    setItems(prev => {
      // A drop onto the OTHER grid is refused: the row would keep its lane and
      // just jump to the end of its own table. Moving between lists is the
      // explicit "⬇ Add to work pad" action, not a drag.
      if (laneOf(prev[from]) !== laneOf(prev[to])) return prev;
      const next = prev.slice();
      const [moving] = next.splice(from, 1);
      next.splice(to, 0, moving);
      const renum = next.map((it, i) => ({ ...it, position: (i + 1) * 10 }));
      renum.forEach((it, i) => dirtyKeysRef.current.add(rowKey(it, i))); // every position changed
      scheduleSave(renum);
      return renum;
    });
  }
  // Pricebook pick (inline typeahead or 🔍 modal) → book price + identity onto
  // the row, exactly like the quote builder's applyPick.
  function applyPick(idx, product, price) {
    // Same signal as the quote builder's pick: the tech overrode the matcher.
    // Fire-and-forget so a logging hiccup never blocks the edit.
    const prev = items[idx] || {};
    apiFetch("/api/quote-brain", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "logMatchCorrection",
        surface: "takeoff",
        sheet_id: sheetId || null,
        from: {
          description: prev.description || null,
          our_cost: Number(prev.unit_cost) || null,
          supplier_name: prev.supplier || null,
          supplier_sku: prev.sku || prev.part_number || null,
          master_product_id: prev.master_product_id || null,
        },
        to: {
          description: price.display_description || price.supplier_description || product.description || null,
          our_cost: Number(price.unit_price) || null,
          supplier_name: price.supplier_name || null,
          supplier_sku: price.supplier_sku || product.sku || null,
          master_product_id: product.master_product_id || null,
        },
      }),
    }).catch(() => { /* never block the edit on a log write */ });
    setItem(idx, {
      // display_description first — the size-enriched form ("3C #6 ACWU; …")
      // composed server-side when the identity lives only in the sku.
      description: price.display_description || price.supplier_description || product.description || items[idx]?.description,
      unit_cost: Number(price.unit_price) || 0,
      master_product_id: product.master_product_id || null,
      supplier: price.supplier_name || items[idx]?.supplier || null,
      // Part # = what the tech says at the counter, so the supplier's PRINTED
      // item number wins over the scraped sku (which is often a description
      // token — "N125CL", "TECK", "STEEL"). sku stays the scraped value: it's
      // the matcher / expense-autolink key, not something a human reads.
      part_number: price.supplier_cat_code || price.mfg_part_number || price.supplier_sku || product.sku || items[idx]?.part_number || null,
      sku: price.supplier_sku || product.sku || null,
    });
    setSearchIdx(null); setInline(null);
  }
  const suggestable = (it) => !it.master_product_id;
  // Scope-of-work / notes edits persist onto the sheet AND mirror into the
  // Jobber job's instructions so both sides stay congruent.
  async function saveScopeDoc(patch) {
    const next = { ...(sheet.scope_doc || {}), ...patch };
    setData(d => ({ ...d, sheet: { ...d.sheet, scope_doc: next } }));
    try {
      const r = await api("updateSheet", { sheetId, patch: { scope_doc: next }, syncJobber: true });
      if (r.jobberSync?.ok) setMsg("Scope saved + synced to the Jobber job ✓");
      else if (r.jobberSync?.error) setMsg("Scope saved (Jobber sync failed: " + r.jobberSync.error + ")");
    } catch (e) { setErr("Scope save failed: " + e.message); }
  }
  async function pullScopeFromJobber() {
    try {
      const r = await api("pullJobberScope", { sheetId });
      setMsg(r.ok ? `Pulled ${r.scope} scope line${r.scope === 1 ? "" : "s"} from the Jobber job ✓` : (r.skipped || r.error));
      // Freshly pulled lines must never land invisible behind an active filter.
      if (r.ok) { clearFilters(); await load(); }
    } catch (e) { setMsg("⚠ " + e.message); }
  }
  async function setSheetSupplier(v) {
    const val = v || null;
    setData(d => ({ ...d, sheet: { ...d.sheet, preferred_supplier: val } }));
    if (val) setRepriceSupplier(val);
    try { await api("updateSheet", { sheetId, patch: { preferred_supplier: val } }); }
    catch (e) { setErr(e.message); }
  }
  async function sendChat() {
    const m = chatInput.trim();
    if (!m || chatBusy) return;
    // Stop the mic first: a live recognizer would write the sentence just
    // sent straight back into the cleared box, and a second Send would add
    // the same material twice.
    if (dictating === "top") { try { dictRef.current?.stop(); } catch { /* already stopped */ } setDictating(null); }
    setChatBusy(true); setChatMsgs(prev => [...prev, { r: "user", t: m }]); setChatInput("");
    setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    try {
      const r = await api("chat", { sheetId, message: m, techName: tech.name });
      setChatMsgs(prev => [...prev, { r: "ai", t: r.reply, ops: (r.applied || []).length }]);
      if (r.needsReload) await load();
    } catch (e) { setChatMsgs(prev => [...prev, { r: "ai", t: "⚠ " + e.message }]); }
    finally { setChatBusy(false); setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50); }
  }
  // The work pad's own assistant — same endpoint, lane:"pad": adds land on the
  // pad unpriced, edits/removes only touch pad lines, and its turns are tagged
  // so the two threads never bleed into each other.
  async function sendPadChat() {
    const m = padChatInput.trim();
    if (!m || padChatBusy) return;
    if (dictating === "pad") { try { dictRef.current?.stop(); } catch { /* already stopped */ } setDictating(null); }
    setPadChatBusy(true); setChatMsgs(prev => [...prev, { r: "user", t: m, lane: "pad" }]); setPadChatInput("");
    setTimeout(() => padChatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
    try {
      await flushSave(); // a stale autosave after the server insert would not revert it, but a pending edit must land first
      const r = await api("chat", { sheetId, message: m, techName: tech.name, lane: "pad" });
      setChatMsgs(prev => [...prev, { r: "ai", t: r.reply, ops: (r.applied || []).length, lane: "pad" }]);
      if (r.needsReload) { clearFilters(); await load(); }
    } catch (e) { setChatMsgs(prev => [...prev, { r: "ai", t: "⚠ " + e.message, lane: "pad" }]); }
    finally { setPadChatBusy(false); setTimeout(() => padChatEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50); }
  }
  // 🎤 Talk it in. Interim results stream into the box so the tech sees it
  // hearing them; whatever was already typed stays in front.
  function toggleDictation(which, getBase, setText) {
    if (dictating) { try { dictRef.current?.stop(); } catch { /* already stopped */ } setDictating(null); return; }
    if (!SpeechRec) return;
    let rec;
    try { rec = new SpeechRec(); } catch { return; }
    const base = String(getBase() || "").trim();
    rec.lang = "en-CA"; rec.interimResults = true; rec.continuous = true; rec.maxAlternatives = 1;
    let finalText = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = String(e.results[i][0]?.transcript || "");
        if (e.results[i].isFinal) finalText += (finalText ? " " : "") + t.trim(); else interim += t;
      }
      const heard = (finalText + (interim ? " " + interim : "")).replace(/\s+/g, " ").trim();
      setText(base ? `${base} ${heard}` : heard);
    };
    rec.onend = () => setDictating(cur => (cur === which ? null : cur));
    rec.onerror = () => setDictating(cur => (cur === which ? null : cur));
    dictRef.current = rec;
    setDictating(which);
    try { rec.start(); } catch { setDictating(null); }
  }
  const micBtn = (which, getBase, setText) => (SpeechRec ? (
    <button type="button" onClick={() => toggleDictation(which, getBase, setText)}
      title={dictating === which ? "Stop listening" : "Talk it in — dictate into this box"}
      style={{ ...btn(), padding: "7px 10px", ...(dictating === which ? { background: C.red, borderColor: C.red, color: "#fff" } : {}) }}>
      {dictating === which ? "⏹" : "🎤"}
    </button>
  ) : null);
  // _ck first: a new row's key must not CHANGE when it adopts its server id,
  // or its selection/dirty state silently detaches mid-edit.
  const rowKey = (it, i) => it._ck || it.id || `new_${i}`;
  const selItems = items.filter((it, i) => selected.has(rowKey(it, i)) && !it.is_header && it.id);
  // Post-flush selection read — after `await flushSave()` the adopted ids live
  // in itemsRef before React re-renders, so handlers see fresh ids (render-time
  // selItems silently DROPPED just-added rows from orders/reprices/bulk ops).
  const getSel = () => itemsRef.current.filter((it, i) => selected.has(rowKey(it, i)) && !it.is_header && it.id);

  // ── filtering ──────────────────────────────────────────────────────────────
  // The grid ALWAYS maps the full items[] array; a filter only sets
  // display:"none" on the row. See the comment above the map in the render —
  // every row handler and the dirty-key bookkeeping are index-based, so a
  // filtered array would silently drop edits from the save payload.
  const filterOn = !!(qTokens.length || fStatus || fSupplier || fColor);

  // Both sets in one pass — avoids an O(n²) indexOf in the select-all checkbox.
  // visibleKeys spans BOTH grids (it only drives display:none and the
  // selected-pruning invariant). visibleSelKeys is the QUOTE grid's select-all
  // / apply-to-all-shown set and visiblePadKeys the work pad's: a select-all
  // ticked in one table must never reach rows that live in the other.
  const { visibleKeys, visibleSelKeys, visiblePadKeys } = useMemo(() => {
    const vis = new Set(), sel = [], padSel = [];
    const pushSel = (it, k) => { if (it.is_header) return; if (laneOf(it) === "pad") padSel.push(k); else sel.push(k); };
    if (!filterOn) {
      items.forEach((it, i) => { const k = rowKey(it, i); vis.add(k); pushSel(it, k); });
      return { visibleKeys: vis, visibleSelKeys: sel, visiblePadKeys: padSel };
    }
    const keep = new Array(items.length).fill(false);
    items.forEach((it, i) => {
      if (it.is_header) return;
      if (fStatus && (it.status || "needed") !== fStatus) return;
      if (fSupplier) { const s = normSup(it.supplier); if (fSupplier === NO_SUPPLIER ? !!s : s !== fSupplier) return; }
      if (fColor) { const c = hlOf(it); if (fColor === ANY_COLOR ? !c : c !== fColor) return; }
      if (!rowMatches(it, qTokens)) return;
      keep[i] = true;
    });
    // Pin the row being edited / just added so it cannot vanish under the cursor.
    if (stickyKey) { const si = items.findIndex((x, i) => rowKey(x, i) === stickyKey); if (si >= 0) keep[si] = true; }
    // Keep a section header whenever anything under it survived — headers are
    // context, and openSavePackage's backward walk reads them.
    for (let i = 0; i < items.length; i++) {
      if (!items[i].is_header) continue;
      for (let j = i + 1; j < items.length && !items[j].is_header; j++) if (keep[j]) { keep[i] = true; break; }
    }
    items.forEach((it, i) => { if (!keep[i]) return; const k = rowKey(it, i); vis.add(k); pushSel(it, k); });
    return { visibleKeys: vis, visibleSelKeys: sel, visiblePadKeys: padSel };
    // `items` MUST stay in these deps: a bulk status change mutates row DATA,
    // which changes what the filter matches. Without it the pruning effect
    // below never re-runs and `selected` keeps keys for rows now hidden —
    // Delete would then remove lines the tech cannot see.
  }, [items, qTokens, fStatus, fSupplier, fColor, stickyKey, filterOn]);

  // Keep `selected` ⊆ visible. This single invariant is what makes selItems,
  // getSel, removeSelected, the bulk ops, openSavePackage and the order payload
  // correct under a filter with no change to any of their bodies — a filtered
  // view can never bulk-edit or delete a row the tech cannot see. It also
  // prunes keys for rows that disappeared across a load().
  useEffect(() => {
    setSelected(prev => {
      if (!prev.size) return prev;
      let dropped = false;
      const next = new Set();
      for (const k of prev) { if (visibleKeys.has(k)) next.add(k); else dropped = true; }
      if (dropped) setPendingBulk(null);   // a staged count is stale the moment the set changes
      return dropped ? next : prev;        // identity return = no render loop
    });
  }, [visibleKeys]);

  // Facet counts come from THIS sheet's rows, not the global supplier list.
  const facets = useMemo(() => {
    const st = {}, sup = new Map(), colors = new Set();
    let noSup = 0;
    for (const it of items) {
      if (it.is_header) continue;
      const k = it.status || "needed";
      st[k] = (st[k] || 0) + 1;
      const s = normSup(it.supplier);
      if (!s) noSup++;
      else { const e = sup.get(s) || { label: it.supplier, n: 0 }; e.n++; sup.set(s, e); }
      const c = hlOf(it);
      if (c) colors.add(c);
    }
    return {
      st, noSup, colors: [...colors],
      sup: [...sup.entries()].sort((a, b) => String(a[1].label).localeCompare(String(b[1].label))),
    };
  }, [items]);

  const clearFilters = () => { setQ(""); setQTokens([]); setFStatus(""); setFSupplier(""); setFColor(""); };
  // Releases the row pin too, so a filtered view resumes hiding it normally.
  const closeLocAsk = (k) => { setLocAsk(null); setStickyKey(cur => (cur === k ? null : cur)); };

  // ── custom columns ─────────────────────────────────────────────────────────
  async function addColumn() {
    const label = window.prompt("New column name:");
    if (!label) return;
    const key = "c_" + label.toLowerCase().replace(/[^a-z0-9]+/g, "_").slice(0, 20);
    const cols = [...(Array.isArray(sheet.columns) ? sheet.columns : []), { key, label, width: 120, custom: true }];
    await api("updateSheet", { sheetId, patch: { columns: cols } });
    setData(d => ({ ...d, sheet: { ...d.sheet, columns: cols } }));
  }
  async function removeColumn(key) {
    if (!window.confirm("Remove this column? (Values stay saved on the rows.)")) return;
    const cols = (Array.isArray(sheet.columns) ? sheet.columns : []).filter(c => c.key !== key);
    await api("updateSheet", { sheetId, patch: { columns: cols } });
    setData(d => ({ ...d, sheet: { ...d.sheet, columns: cols } }));
  }

  // ── supplier reprice ───────────────────────────────────────────────────────
  async function repriceNow() {
    if (!repriceSupplier) return;
    setRepriceBusy(true); setRepriceMsg(null);
    try {
      await flushSave();
      const sel = getSel();
      const ids = sel.length ? sel.map(i => i.id) : undefined;
      const r = await api("reprice", { sheetId, supplier: repriceSupplier, itemIds: ids, apply: true });
      setRepriceMsg(`${repriceSupplier}: matched ${r.matched}/${r.total} line${r.total === 1 ? "" : "s"} — the rest kept their current supplier/price.`);
      await load();
    } catch (e) { setRepriceMsg("⚠ " + e.message); }
    finally { setRepriceBusy(false); }
  }

  // ── bulk status/location/supplier ──────────────────────────────────────────
  // Every bulk edit is STAGED first (setPendingBulk) and only applied from the
  // confirm strip. One mis-tap on a tablet used to change N rows instantly.
  const BULK_FIELD_LABEL = { status: "status", location: "location", supplier: "supplier" };

  function stageBulk(scope, field, value, label) {
    const count = scope === "filtered" ? visibleSelKeys.length : selItems.length;
    if (!count) return;
    // Receiving a batch is the moment you know where it all went — the confirm
    // strip asks for the location right there. null = not a located status, so
    // no field is shown.
    const wantsLocation = field === "status" && LOCATED_STATUSES.has(value);
    setPendingBulk({ scope, field, value, label, count, location: wantsLocation ? "" : null });
  }

  async function applyPendingBulk() {
    const p = pendingBulk;
    if (!p) return;
    setPendingBulk(null);
    // Snapshot the key set BEFORE any await — `selected` can change under us.
    const keys = new Set(p.scope === "filtered" ? visibleSelKeys : [...selected]);
    if (!keys.size) return;
    if (p.field === "__delete__") { doRemoveKeys(keys); return; }
    if (p.scope === "filtered" && !window.confirm(
      `Set ${BULK_FIELD_LABEL[p.field] || p.field} = "${p.label}" on all ${keys.size} filtered line(s)?\n\n` +
      `This includes lines scrolled off screen.` +
      (p.field === "supplier" ? `\n\nThis overwrites each line's own supplier.` : ""))) return;
    // MANDATORY: the 1600ms autosave timer firing after markItems would revert it.
    await flushSave();
    // Post-flush read — adopted server ids live in itemsRef before React re-renders.
    const rows = itemsRef.current.filter((it, i) => keys.has(rowKey(it, i)) && !it.is_header && it.id);
    if (!rows.length) { setMsg("Nothing to update — those lines aren't saved yet."); return; }
    // One patch may carry more than one field (status + location on a receive),
    // so undo snapshots every field being written, not just the headline one.
    const patch = { [p.field]: p.value };
    if (p.location) patch.location = p.location;
    const fields = Object.keys(patch);
    const before = rows.map(r => ({ id: r.id, prev: Object.fromEntries(fields.map(f => [f, r[f] ?? null])) }));
    try {
      await api("markItems", { itemIds: rows.map(r => r.id), patch });
      // Merge by ID, not by index — the last index-basis question in the bulk path.
      const ids = new Set(rows.map(r => r.id));
      setItems(prev => prev.map(it => (it.id && ids.has(it.id) ? { ...it, ...patch } : it)));
      const g = new Map();
      for (const b of before) {
        const key = JSON.stringify(b.prev);
        if (!g.has(key)) g.set(key, { prev: b.prev, ids: [] });
        g.get(key).ids.push(b.id);
      }
      const what = `${p.label}${p.location ? ` @ ${p.location}` : ""}`;
      setBulkUndo({ label: `${rows.length} line(s) → ${what}`, groups: [...g.values()] });
      setMsg(`✓ ${rows.length} line(s) → ${what}`);
    } catch (e) { setErr(e.message); }
  }

  // Undo is worth more than a scarier confirm — it makes the mistake cheap
  // instead of trying to make it impossible.
  async function undoBulk() {
    const u = bulkUndo;
    if (!u) return;
    setBulkUndo(null);
    await flushSave();
    try {
      for (const grp of u.groups) await api("markItems", { itemIds: grp.ids, patch: grp.prev });
      const map = new Map();
      for (const grp of u.groups) for (const id of grp.ids) map.set(id, grp.prev);
      setItems(prev => prev.map(it => (it.id && map.has(it.id) ? { ...it, ...map.get(it.id) } : it)));
      setMsg("Undone ✓");
    } catch (e) { setErr(e.message); }
  }

  // Manual row colour. `custom` is ONE jsonb object per row and is not on
  // markItems' whitelist, so a PATCH would replace the whole object and wipe
  // every custom-column value. Merge locally and let the existing debounce be
  // the single writer (same shape as setCustom). No flushSave: flush-first is
  // the rule for SERVER mutations; flushing a local edit would serialise every
  // swatch click behind a network round-trip.
  function bulkHighlight(keys, hex) {
    const ks = new Set(keys);
    setHlOpen(null);
    setItems(prev => {
      let touched = false;
      const next = prev.map((it, i) => {
        const k = rowKey(it, i);
        if (it.is_header || !ks.has(k)) return it;
        const custom = { ...(it.custom || {}) };
        if (hex) custom[HL_KEY] = hex; else delete custom[HL_KEY];
        dirtyKeysRef.current.add(k);
        touched = true;
        return { ...it, custom };
      });
      if (touched) scheduleSave(next);
      return touched ? next : prev;
    });
  }

  // ── save selection as a material package ───────────────────────────────────
  // Snapshot the selected lines (identity + pricing knobs — never job state
  // like status/actuals) and prefill the name from the section header above
  // the first selected row: "select under a heading → that's the package".
  function openSavePackage() {
    const first = items.findIndex((it, i) => selected.has(rowKey(it, i)) && !it.is_header);
    if (first < 0) return;
    const snapshot = items
      .filter((it, i) => selected.has(rowKey(it, i)) && !it.is_header && String(it.description || "").trim())
      .map(it => ({
        description: it.description, purpose: it.purpose, qty: it.qty, unit: it.unit,
        part_number: it.part_number, sku: it.sku, supplier: it.supplier,
        unit_cost: it.unit_cost, markup_pct: it.markup_pct, pst_pct: it.pst_pct,
        master_product_id: it.master_product_id, notes: it.notes,
      }));
    if (!snapshot.length) { setMsg("Give the selected lines a description first — blank rows can't be packaged."); return; }
    let heading = "";
    for (let i = first - 1; i >= 0; i--) if (items[i].is_header) { heading = String(items[i].description || "").trim(); break; }
    setPkgSave({ defaultName: heading, items: snapshot });
  }

  // ── work pad: send quote lines down, import a list ─────────────────────────
  // Stage first (same as every bulk op). Nothing from the quote reaches the pad
  // unless the tech ticks it; by default only qty + description come down —
  // the quote's part numbers are the thing they said is usually wrong.
  function stagePad() {
    const keys = new Set();
    items.forEach((it, i) => { if (selected.has(rowKey(it, i)) && !it.is_header && laneOf(it) !== "pad") keys.add(rowKey(it, i)); });
    if (!keys.size) return;
    setPendingPad({ keys, count: keys.size, carry: false });
  }
  function addToPad(keys, carry) {
    setPendingPad(null);
    const src = itemsRef.current;
    const have = new Set(src.filter(it => laneOf(it) === "pad").map(it => it.custom?._src).filter(Boolean));
    let pos = src.reduce((m, it) => Math.max(m, Number(it.position) || 0), 0);
    const add = [];
    let already = 0;
    src.forEach((it, i) => {
      if (!keys.has(rowKey(it, i)) || it.is_header || laneOf(it) === "pad") return;
      if (it.id && have.has(it.id)) { already++; return; }
      const ck = "ck_" + Math.random().toString(36).slice(2) + (Date.now() + add.length).toString(36);
      add.push({
        _ck: ck, sheet_id: sheetId, lane: "pad", position: (pos += 10), is_header: false,
        qty: it.qty ?? 1, unit: it.unit || "ea", description: it.description || "", purpose: it.purpose || null,
        notes: null, status: "needed", location: null,
        part_number: carry ? (it.part_number || null) : null,
        supplier: carry ? (it.supplier || null) : null,
        unit_cost: carry ? (it.unit_cost ?? null) : null,
        master_product_id: carry ? (it.master_product_id || null) : null,
        markup_pct: null, pst_pct: null, sku: null,
        source: "from_quote", created_by: tech.name,
        // Remembers which grid line it came from → the "↓ pad" marker up top.
        custom: it.id ? { _src: it.id } : {},
      });
    });
    if (!add.length) { setMsg(already ? "Those lines are already on the work pad." : "Nothing to send down."); return; }
    add.forEach(r => dirtyKeysRef.current.add(r._ck));
    setItems(prev => { const next = [...prev, ...add]; scheduleSave(next); return next; });
    setSelected(new Set());
    setMsg(`⬇ ${add.length} line${add.length === 1 ? "" : "s"} sent down to the work pad${carry ? " with the quote's part # / supplier / cost" : " (qty + description only)"}${already ? ` · ${already} already there` : ""}.`);
  }
  // Lines the pad already holds, by their grid source id.
  const padSrcIds = useMemo(() => new Set(items.filter(it => laneOf(it) === "pad").map(it => it.custom?._src).filter(Boolean)), [items]);

  async function afterPadImport(r) {
    clearFilters();
    await load();
    setPadUndo(r.ids?.length ? { ids: r.ids, n: r.added } : null);
    setMsg(`✓ ${r.added} line${r.added === 1 ? "" : "s"} added to the work pad${r.headers ? ` under ${r.headers} heading${r.headers === 1 ? "" : "s"}` : ""}${r.used === "fallback" ? " — split line by line (the AI clean-up was unavailable)" : ""}.${(r.warnings || []).length ? " " + r.warnings.join(" ") : ""}`);
  }
  async function padImportText(text, fileName = null) {
    const t = String(text || "").trim();
    if (!t || padBusy) return;
    setPadBusy(fileName ? `Reading ${fileName}…` : "Cleaning up the list…");
    try {
      await flushSave(); // server insert — a pending edit must land first so positions line up
      const r = await api("padImport", { sheetId, text: t, fileName, source: fileName ? "file" : "paste", techName: tech.name });
      await afterPadImport(r);
    } catch (e) { setMsg("⚠ " + e.message); }
    finally { setPadBusy(null); }
  }
  async function padImportFile(f) {
    if (!f || padBusy) return;
    const ext = (f.name.split(".").pop() || "").toLowerCase();
    const isText = /^text\//.test(f.type || "") || /^(csv|tsv|txt|md)$/.test(ext);
    const isPdf = f.type === "application/pdf" || ext === "pdf";
    const isImg = /^image\/(jpeg|jpg|png|gif|webp)$/.test(f.type || "");
    if (!isText && !isPdf && !isImg) { setMsg("⚠ Drop a PDF, a photo, or a text / CSV file."); return; }
    if (f.size > PAD_MAX_BYTES) { setMsg("⚠ That file is over 9 MB — send the PDF rather than a scan, or split it."); return; }
    if (isText) { padImportText(await f.text(), f.name); return; }
    setPadBusy(`Reading ${f.name}…`);
    try {
      await flushSave();
      const safe = f.name.replace(/[^a-z0-9._-]+/gi, "_").slice(-80);
      const storagePath = `supplier-quotes/takeoff-${sheetId}/${Date.now().toString(36)}-${safe}`;
      const { error } = await supabase.storage.from(PAD_BUCKET).upload(storagePath, f, { contentType: f.type || "application/pdf", upsert: true });
      if (error) throw new Error(`Upload failed: ${error.message || error}`);
      const r = await api("padImport", { sheetId, storagePath, bucket: PAD_BUCKET, fileName: f.name, mediaType: f.type || "application/pdf", source: "file", techName: tech.name });
      await afterPadImport(r);
    } catch (e) { setMsg("⚠ " + e.message); }
    finally { setPadBusy(null); }
  }
  async function undoPadImport() {
    const u = padUndo;
    if (!u?.ids?.length) return;
    setPadUndo(null);
    try {
      await flushSave();
      await api("saveItems", { sheetId, upserts: [], deletes: u.ids });
      await load();
      setMsg(`Removed the ${u.n} imported line${u.n === 1 ? "" : "s"}.`);
    } catch (e) { setErr(e.message); }
  }
  // Drop target for the whole pad card. A row-reorder drag carries no payload
  // and is handled (and stopped) by the row itself. Two more drags must NOT
  // become imports: text selected and dragged from one of the pad's own
  // inputs (padSelfDragRef — dragstart bubbles from the input to the card),
  // and text dropped INTO an input or textarea, where the browser's native
  // insert is what the tech wants. Files are claimed wherever they land.
  const isEditable = (el) => !!el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
  const hasFiles = (e) => Array.from((e.dataTransfer && e.dataTransfer.types) || []).includes("Files");
  const padDrop = {
    onDragStart: () => { padSelfDragRef.current = true; },
    onDragEnd: () => { padSelfDragRef.current = false; },
    onDragOver: (e) => {
      if (!dtHasPayload(e)) return;
      if (!hasFiles(e) && (padSelfDragRef.current || isEditable(e.target))) return;
      e.preventDefault(); if (!padOver) setPadOver(true);
    },
    onDragLeave: (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setPadOver(false); },
    onDrop: (e) => {
      if (!dtHasPayload(e)) return;
      if (!hasFiles(e) && (padSelfDragRef.current || isEditable(e.target))) { setPadOver(false); return; }
      e.preventDefault(); setPadOver(false);
      if (dragFromRef.current != null) return;
      const f = e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) { padImportFile(f); return; }
      const t = e.dataTransfer.getData("text/plain");
      if (t && t.trim()) padImportText(t);
    },
  };

  // ── van/sea-can stock check on selection ───────────────────────────────────
  async function checkStock() {
    await flushSave();
    const qs = getSel().filter(i => String(i.description || "").trim()).map(i => ({ id: i.id, description: i.description }));
    if (!qs.length) return;
    try {
      const r = await api("stockSearch", { queries: qs });
      const map = {};
      for (const res of (r.results || [])) if (res.matches?.length) map[res.id] = res.matches;
      setStockMatches(m => ({ ...m, ...map }));
      setMsg(`Stock check: ${Object.keys(map).length} of ${qs.length} selected item${qs.length === 1 ? "" : "s"} might already be in a van / the sea can — see the green strips.`);
    } catch (e) { alert(e.message); }
  }
  async function takeStock(it, m) {
    try {
      await flushSave(); // a stale autosave after useStock would revert status/location
      const r = await api("useStock", { itemId: it.id, stockId: m.stockId, qty: it.qty, techName: tech.name });
      setItems(prev => prev.map(x => (x.id === it.id ? { ...x, status: "stock", location: r.location } : x)));
      setStockMatches(prev => { const n = { ...prev }; delete n[it.id]; return n; });
      setMsg(`Marked as stock from ${r.location}${r.stockQtyLeft != null ? ` — ${r.stockQtyLeft} left there` : ""}.`);
    } catch (e) { alert(e.message); }
  }
  async function resolveRev(it, accept) {
    try { await flushSave(); await api("resolveReview", { itemId: it.id, accept }); setReviewOpen(null); await load(); }
    catch (e) { alert(e.message); }
  }
  async function linkInv(invId) {
    const orderId = linkSel[invId];
    if (!orderId) return;
    try {
      await flushSave();
      const r = await api("linkExpense", { orderId, pendingInvoiceId: invId });
      setMsg(`Invoice linked${r.itemsPriced ? ` — ${r.itemsPriced} actual price${r.itemsPriced === 1 ? "" : "s"} filled` : ""}${r.suggested ? `, ${r.suggested} to review ($? chips)` : ""}.`);
      await load();
    } catch (e) { alert(e.message); }
  }

  if (err && !data) return <div style={{ color: C.red, padding: 20 }}>{err} <button style={btn()} onClick={onBack}>Back</button></div>;
  if (!data) return <div style={{ color: C.muted, padding: 20 }}>Loading takeoff…</div>;

  // Totals per the Log Book formulas: our cost is PST-in, profit = client − our.
  // Under a filter these cover only the VISIBLE lines — a 3-row view showing the
  // whole sheet's $40k is a misread waiting to happen. The footer says so.
  // Two sets in ONE pass: `all` (every line in view, as before) and `sel` (only
  // the ticked lines) so ticking the checkboxes narrows the footer to what you
  // picked. `selected` is pruned to visible rows by the effect above, so a
  // ticked line is always one you can actually see.
  // `our` is the real cost basis (PST in) and drives profit + margin, so a tax
  // toggle can never flatter them. `shown` is what the footer prints and follows
  // the boxes. `ex` is the ex-tax cost, the only fair partner for `actual`
  // (invoice LINE totals are ex-tax).
  // Work-pad rows never count here: the pad is an ordering list, and a cost a
  // tech happened to jot on one must not move the sheet's quoted-vs-actual.
  const { all: totals, sel: selTotals, selLines } = items.reduce((a, it, i) => {
    if (it.is_header || laneOf(it) === "pad") return a;
    const k = rowKey(it, i);
    if (filterOn && !visibleKeys.has(k)) return a;
    const our = ourTotalOf(it), ex = ourExTotal(it), shown = ourShownTotal(it, taxBasis);
    const client = clientTotalOf(it), actual = Number(it.actual_total) || 0;
    a.all.our += our; a.all.ex += ex; a.all.shown += shown; a.all.client += client; a.all.actual += actual;
    if (selected.has(k)) { a.sel.our += our; a.sel.ex += ex; a.sel.shown += shown; a.sel.client += client; a.sel.actual += actual; a.selLines++; }
    return a;
  }, { all: { our: 0, ex: 0, shown: 0, client: 0, actual: 0 }, sel: { our: 0, ex: 0, shown: 0, client: 0, actual: 0 }, selLines: 0 });
  const marginPctOf = (t) => (t.client > 0 ? ((t.client - t.our) / t.client) * 100 : 0);
  // Say what profit came from whenever the printed cost is on a different basis.
  const basisNoteOf = (t) => (taxBasis.pst && !taxBasis.gst ? "" : `, on cost incl. PST ${money(t.our)}`);
  const taxBox = (on, onChange, text, hint) => (
    <label title={hint} style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer", color: on ? C.lime : C.muted, fontWeight: on ? 700 : 400 }}>
      <input type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: C.lime, cursor: "pointer" }} />
      {text}
    </label>
  );
  const taxToggles = (
    <span style={{ display: "inline-flex", gap: 14, alignItems: "center", fontSize: 11.5, fontWeight: 400 }}>
      <span style={{ color: C.muted }}>Our cost shown:</span>
      {taxBox(taxBasis.pst, (v) => changeTaxBasis({ pst: v }), "+ PST", "Add each line's own PST % — PST is real money we pay, and it is what margin already uses.")}
      {taxBox(taxBasis.gst, (v) => changeTaxBasis({ gst: v }), "+ GST 5%", "Add 5% GST so the figure matches an invoice total. GST comes back as an input tax credit, so it never changes profit or margin.")}
      {!taxBasis.pst && !taxBasis.gst ? <span style={{ color: C.muted, fontStyle: "italic" }}>ex tax — same basis suppliers quote on</span> : null}
    </span>
  );
  const bodyLineCount = items.filter(x => !x.is_header && laneOf(x) !== "pad").length;
  const gridRowCount = items.filter(x => laneOf(x) !== "pad").length;   // headers included — drives the empty state
  const padRowCount = items.filter(x => laneOf(x) === "pad").length;
  const padStats = items.reduce((a, x) => {
    if (x.is_header || laneOf(x) !== "pad") return a;
    a.lines++;
    const s = x.status || "needed";
    if (s === "ordered" || s === "backorder") a.ordered++;
    else if (s === "received" || s === "installed" || s === "stock") a.done++;
    else a.open++;
    return a;
  }, { lines: 0, ordered: 0, done: 0, open: 0 });
  const selQuoteCount = selItems.filter(it => laneOf(it) !== "pad").length;
  const padChat = chatMsgs.filter(m => m.lane === "pad");
  // Quote requests share the material_orders table but are not orders: no PO,
  // no invoice, and they must never appear in the "link an invoice to an
  // order" picker or a tech could hand-link an expense to a question.
  const orderRows = (data.orders || []).filter(o => o.kind !== "rfq");
  const rfqRows = (data.orders || []).filter(o => o.kind === "rfq");
  const colSpanAll = 19 + customCols.length;   // +1 Purpose, +1 Our total
  // Work pad: ⠿ ☐ Status Qty Unit Description Part# Note (+ Supplier Cost Location Purpose) ✕
  const colSpanPad = 9 + (padMore ? 4 : 0);

  // Numeric cells commit on BLUR from the free-typed string. The old controlled
  // value={Number(...)} pattern re-parsed every keystroke, which ate the decimal
  // point mid-entry: typing "12.50" landed as 1250 — a 100× cost error. The key
  // includes the stored value so an external update (reprice/reload) remounts
  // the cell with fresh data, while typing (uncontrolled) never re-renders it.
  const numCell = (it, i, field) => (
    <input inputMode="decimal"
      key={`${rowKey(it, i)}_${field}_${it[field] ?? ""}`}
      defaultValue={it[field] ?? ""}
      onBlur={e => {
        const raw = e.target.value.trim().replace(",", ".");
        const n = raw === "" ? null : Number(raw);
        if (raw !== "" && !isFinite(n)) { e.target.value = it[field] ?? ""; return; } // unparseable → revert
        if ((n ?? null) !== (it[field] ?? null)) setItem(i, { [field]: n });
      }}
      style={{ ...inputStyle, textAlign: "right" }} placeholder="—" />
  );

  // Colour swatches — used by the selection bar, the apply-to-filtered bar and
  // the per-row popover. Painting goes through the normal debounced save.
  const swatchStrip = (keys, title) => (
    <span style={{ display: "flex", gap: 3, alignItems: "center" }} title={title}>
      {HL_COLORS.map(c => (
        <span key={c.hex} onClick={() => bulkHighlight(keys, c.hex)} title={c.label}
          style={{ width: 15, height: 15, borderRadius: 4, cursor: "pointer", background: c.hex, border: `1px solid ${C.border}` }} />
      ))}
      <span onClick={() => bulkHighlight(keys, null)} title="Clear highlight"
        style={{ width: 15, height: 15, borderRadius: 4, cursor: "pointer", border: `1px solid ${C.border}`, color: C.muted, fontSize: 10, textAlign: "center", lineHeight: "14px" }}>✕</span>
    </span>
  );

  // One status cell for BOTH grids: the chip-coloured select plus the "where is
  // it?" prompt that opens the moment material lands. The work pad marks off
  // the same stages with the same colours as the quote grid.
  const statusCell = (it, i, k, table = "grid") => {
    const st = statusOf(it.status);
    const txt = st.text || st.color;
    // Once it has physically landed, this column should answer "where is it?"
    // — the status itself is already obvious from the black chip.
    const where = LOCATED_STATUSES.has(it.status) ? String(it.location || "").trim() : "";
    return (
      <td {...rc(table, i, 0, { padding: "2px 3px" })}>
        <div style={{ position: "relative" }}>
          <select value={it.status || "needed"}
            title={where ? `${st.label} — ${where}` : st.label}
            onChange={e => {
              const v = e.target.value;
              setItem(i, { status: v });
              // The moment material lands is the only moment anyone actually
              // knows where it went. Ask then, instead of hoping the Location
              // column gets filled in afterwards. stickyKey pins the row so an
              // active filter can't yank it (and the prompt) out from under
              // the cursor.
              if (LOCATED_STATUSES.has(v)) { setStickyKey(k); setLocAsk({ key: k, idx: i }); }
            }}
            style={{ ...inputStyle, color: txt, fontWeight: 700, background: st.text ? st.color : st.color + "14", border: `1px solid ${st.color}${st.text ? "" : "44"}`, padding: "4px 2px" }}>
            {STATUSES.map(s => (
              <option key={s.key} value={s.key} style={{ background: C.card, color: s.text || s.color }}>
                {s.key === it.status && where ? `📍 ${where}` : s.label}
              </option>
            ))}
          </select>
          {locAsk?.key === k && (
            <div style={{ position: "absolute", zIndex: 75, top: "100%", left: 0, minWidth: 230, background: C.surface, border: `1px solid ${C.lime}66`, borderRadius: 8, padding: 8, boxShadow: "0 8px 24px rgba(0,0,0,.55)" }}>
              <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 6 }}>Where is it?</div>
              <input autoFocus list="takeoff-locations" defaultValue={it.location || ""}
                placeholder="Kyle's van, trailer, shop…"
                onKeyDown={e => {
                  if (e.key === "Enter") { setItem(i, { location: e.target.value.trim() }); closeLocAsk(k); }
                  if (e.key === "Escape") closeLocAsk(k);
                }}
                onBlur={e => { const v = e.target.value.trim(); if (v !== String(it.location || "")) setItem(i, { location: v }); }}
                style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card, marginBottom: 6 }} />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                {LOCATION_SUGGESTIONS.map(l => (
                  <span key={l} onMouseDown={e => { e.preventDefault(); setItem(i, { location: l }); closeLocAsk(k); }}
                    style={{ ...chip(C.blue), cursor: "pointer" }}>{l}</span>
                ))}
              </div>
              <div style={{ marginTop: 6, textAlign: "right" }}>
                <span onMouseDown={e => { e.preventDefault(); closeLocAsk(k); }} style={{ fontSize: 11.5, color: C.muted, cursor: "pointer", textDecoration: "underline" }}>skip</span>
              </div>
            </div>
          )}
        </div>
      </td>
    );
  };
  // The ⠿ handle + the highlight tab / checkbox cell — identical on both grids.
  const handleCells = (it, i, k, hl) => (
    <>
      <td draggable={!filterOn} onDragStart={e => { dragFromRef.current = i; e.dataTransfer.effectAllowed = "move"; }}
        onDragEnd={() => { dragFromRef.current = null; }}
        style={{ padding: "2px 4px", cursor: filterOn ? "default" : "grab", color: C.muted, userSelect: "none", textAlign: "center", borderLeft: `4px solid ${it.is_header ? C.lime : statusOf(it.status).color}` }}
        title={filterOn ? "Clear the filter to reorder rows" : "Drag to reorder"}>⠿</td>
      <td style={{ padding: "2px 4px", textAlign: "center", position: "relative" }}>
        {!it.is_header && (
          <span onClick={() => setHlOpen(cur => (cur === k ? null : k))} title={hl ? "Change / clear this line's highlight" : "Highlight this line"}
            style={{ position: "absolute", left: 1, top: "50%", transform: "translateY(-50%)", width: 6, height: 20, borderRadius: 3, cursor: "pointer", background: hl || "transparent", border: hl ? "none" : `1px dashed ${C.border}` }} />
        )}
        {!it.is_header && <input type="checkbox" checked={selected.has(k)} onChange={() => setSelected(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; })} />}
        {hlOpen === k && (
          <div style={{ position: "absolute", zIndex: 70, top: "100%", left: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 6, boxShadow: "0 8px 24px rgba(0,0,0,.55)" }}>
            {swatchStrip([k], "Highlight this line")}
          </div>
        )}
      </td>
    </>
  );
  const rowBg = (it, k, hl) => (it.is_header ? "rgba(165,206,58,.06)"
    : selected.has(k) ? "rgba(165,206,58,.04)"
    : hl ? hexA(hl, 0.10)
    // "needed" gets the strongest wash of any status: it's the work that
    // hasn't been sourced yet, so it should be the first thing the eye lands on.
    : tintByStatus ? statusOf(it.status).color + ((it.status || "needed") === "needed" ? "1f" : "12")
    : "none");

  // ── block selection: what each column reads as when copied ────────────────
  // Column ordinals follow the cells in render order (the ordinal passed to
  // rc()). Money columns copy as plain numbers so a sheet can add them up.
  const num2 = (v) => (v ? Number(v).toFixed(2) : "");
  const gridCellText = (it, c) => {
    switch (c) {
      case 0: { const st = statusOf(it.status); const where = LOCATED_STATUSES.has(it.status) ? String(it.location || "").trim() : ""; return st.label + (where ? ` @ ${where}` : ""); }
      case 1: return it.qty ?? "";
      case 2: return it.unit || "";
      case 3: return it.description || "";
      case 4: return it.purpose || "";
      case 5: return it.part_number || "";
      case 6: return it.supplier || "";
      case 7: return it.unit_cost ?? "";
      case 8: return num2(ourShownTotal(it, taxBasis));
      case 9: return it.markup_pct ?? "";
      case 10: return it.pst_pct ?? "";
      case 11: return num2(clientEach(it));
      case 12: return num2(clientTotalOf(it));
      case 13: return it.location || "";
      case 14: return it.notes || "";
      case 15: return it.actual_total ?? "";
      default: { const col = customCols[c - 16]; return col ? ((it.custom || {})[col.key] ?? "") : ""; }
    }
  };
  const padCellText = (it, c) => {
    switch (c) {
      case 0: { const st = statusOf(it.status); const where = LOCATED_STATUSES.has(it.status) ? String(it.location || "").trim() : ""; return st.label + (where ? ` @ ${where}` : ""); }
      case 1: return it.qty ?? "";
      case 2: return it.unit || "";
      case 3: return it.description || "";
      case 4: return it.part_number || "";
      case 5: return it.notes || "";
      case 6: return it.supplier || "";
      case 7: return it.unit_cost ?? "";
      case 8: return it.location || "";
      case 9: return it.purpose || "";
      default: return "";
    }
  };
  // The block as text: rows on the other table and rows hidden by a filter are
  // skipped; a section header inside the block becomes its own line.
  const rangeText = (rng) => {
    if (!rng) return "";
    const list = itemsRef.current;
    const lane = rng.table === "pad" ? "pad" : "quote";
    return rangeToText(rng, list.map((_, i) => i), (r, c) => {
      const it = list[r];
      if (!it || laneOf(it) !== lane) return null;
      if (filterOn && !visibleKeys.has(rowKey(it, r))) return null;
      if (it.is_header) return c === rng.c0 ? String(it.description || "") : "";
      return rng.table === "pad" ? padCellText(it, c) : gridCellText(it, c);
    });
  };
  copyRangeRef.current = async (rng) => {
    const text = rangeText(rng);
    if (!text) { setMsg("Nothing to copy in that block."); return; }
    const rows = text.split("\n").length, cols = rng.c1 - rng.c0 + 1;
    const m = await copyText(text, `Copied ${rows} row${rows === 1 ? "" : "s"} × ${cols} column${cols === 1 ? "" : "s"} ✓ — pastes as columns into a sheet, or as lines anywhere else`);
    setMsg(m);
    if (!/^Copy failed/.test(m)) markCopied();
  };
  const rangeSize = cellRange ? { rows: rangeText(cellRange).split("\n").filter(Boolean).length, cols: cellRange.c1 - cellRange.c0 + 1 } : null;

  // "Already on hand?" strip under a row, on whichever grid the row lives.
  const stockStrip = (it, k, span) => (visibleKeys.has(k) && it.id && stockMatches[it.id] ? (
    <tr style={{ background: "rgba(52,211,153,.07)" }}>
      <td colSpan={span} style={{ padding: "6px 12px", fontSize: 12, color: C.green }}>
        Already on hand?{" "}
        {stockMatches[it.id].map(m => (
          <span key={m.stockId} style={{ marginRight: 14 }}>
            <b>{m.owner}</b>: {m.qty ?? "?"} {m.unit || ""} {m.description}{m.part_number ? ` [${m.part_number}]` : ""}
            <button style={{ ...btn(), padding: "2px 8px", marginLeft: 6, fontSize: 11.5, borderColor: C.green, color: C.green }} onClick={() => takeStock(it, m)}>Use it</button>
          </span>
        ))}
        <span onClick={() => setStockMatches(prev => { const n = { ...prev }; delete n[it.id]; return n; })} style={{ cursor: "pointer", color: C.muted, marginLeft: 4 }}>dismiss</span>
      </td>
    </tr>
  ) : null);
  // Staged bulk confirm. Rendered in the sticky zone for a selection-scope
  // edit (beside the bar that launched it) and in flow beside the filter bar
  // for an apply-to-all-shown edit.
  const pendingBulkStrip = () => (
    <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10, padding: "9px 12px", background: pendingBulk.field === "__delete__" ? "rgba(239,68,68,.10)" : "rgba(245,158,11,.10)", border: `1px solid ${pendingBulk.field === "__delete__" ? "rgba(239,68,68,.55)" : C.amber + "55"}`, borderRadius: 10 }}>
      <span style={{ fontSize: 12.5, color: C.text, fontWeight: 700 }}>
        {pendingBulk.field === "__delete__"
          ? `Delete ${pendingBulk.count} line(s)?`
          : `Set ${BULK_FIELD_LABEL[pendingBulk.field] || pendingBulk.field} of ${pendingBulk.count} line(s) to “${pendingBulk.label}”?`}
      </span>
      {/* Receiving a batch: capture where it went in the same breath. */}
      {pendingBulk.location !== null && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12.5, color: C.muted }}>
          📍
          <input list="takeoff-locations" autoFocus value={pendingBulk.location}
            onChange={e => setPendingBulk(p => ({ ...p, location: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") applyPendingBulk(); }}
            placeholder="Where did it go? (optional)"
            style={{ ...inputStyle, width: 210, border: `1px solid ${C.border}`, background: C.card }} />
        </span>
      )}
      <button style={btn(pendingBulk.field !== "__delete__", pendingBulk.field === "__delete__")} onClick={applyPendingBulk}>
        {pendingBulk.field === "__delete__" ? `Yes — delete ${pendingBulk.count}` : `Yes — ${pendingBulk.count} line(s)`}
      </button>
      <button style={btn()} onClick={() => setPendingBulk(null)}>Cancel</button>
    </div>
  );

  return (
    <div>
      <style>{TK_CSS}</style>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <button style={btn()} onClick={onBack}>← All takeoffs</button>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, letterSpacing: ".03em", color: C.lime }}>
            {sheet.job_number ? `Job #${sheet.job_number}` : "Takeoff"}{sheet.client_name ? ` — ${sheet.client_name}` : ""}
          </div>
          <div style={{ fontSize: 12.5, color: C.muted }}>
            {/* Save state moved to the toolbar indicator — one source of truth. */}
            {sheet.title || ""}{sheet.quote_number ? ` · from quote #${sheet.quote_number}` : ""}
          </div>
        </div>
        <select value={sheet.preferred_supplier || ""} onChange={e => setSheetSupplier(e.target.value)}
          title="The supplier this job is ordering from — the assistant and re-pricing try this supplier's book first, and lines priced elsewhere get flagged."
          style={{ ...inputStyle, width: 175, border: `1px solid ${sheet.preferred_supplier ? "rgba(165,206,58,.5)" : C.border}`, background: C.card, color: sheet.preferred_supplier ? C.lime : C.muted, fontWeight: 700 }}>
          <option value="">Ordering from…</option>
          {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button style={btn()} onClick={() => setShowVanStock(true)}>Van stock</button>
        <button style={btn()} onClick={() => setShowDirectory(true)}>Suppliers ✎</button>
        {sheet.quote_number && <button style={btn()} onClick={async () => { try { const r = await api("seedFromQuote", { sheetId }); setMsg((r.warning ? "⚠ " + r.warning + " — " : "") + `Pulled ${r.seeded} new line${r.seeded === 1 ? "" : "s"} from quote #${sheet.quote_number}`); await load(); } catch (e) { setMsg("⚠ " + e.message); } }}>⟳ Re-pull quote</button>}
      </div>

      {/* Multi-option seed guard: the takeoff seeds the option the CLIENT APPROVED
          (matched by the Jobber total). When that match is uncertain, warn loudly so
          the tech verifies before ordering the wrong length/type of material. */}
      {sheet.scope_doc?.seed_warning && (
        <div style={{ background: "rgba(239,68,68,.12)", border: "1px solid rgba(239,68,68,.5)", borderRadius: 10, padding: "10px 13px", marginBottom: 12, fontSize: 13, color: "#fecaca", fontWeight: 600 }}>
          ⚠ {sheet.scope_doc.seed_warning}
        </div>
      )}
      {sheet.scope_doc?.seeded_option && !sheet.scope_doc?.seed_warning && (
        <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>Seeded from the client-approved option: <b style={{ color: C.text }}>{sheet.scope_doc.seeded_option}</b></div>
      )}

      {/* Pipeline flow-through — request → assessment → quote → job → invoices */}
      <FlowStrip anchor={{ sheet: sheetId }} style={{ marginBottom: 12 }} />

      {/* Scope of work + notes — always here (this is where the tech plans the
          job), editable, one line per bullet. Saved on blur. */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 14px", marginBottom: 12, fontSize: 13, color: C.text }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setScopeOpen(v => !v)}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 17, color: C.lime, flex: 1 }}>
            Scope of work {sheet.scope_doc?.title ? <span style={{ color: C.muted, fontFamily: "'Roboto',sans-serif", fontSize: 12.5, fontWeight: 400 }}>— {sheet.scope_doc.title}</span> : null}
          </div>
          {sheet.jobber_job_id && (
            <button style={{ ...btn(), fontSize: 11.5, padding: "4px 9px" }} onClick={e => { e.stopPropagation(); pullScopeFromJobber(); }}
              title="Pull the Scope of Work section off the Jobber job's line items / instructions (for jobs quoted directly in Jobber)">⟲ Pull from Jobber</button>
          )}
          <span style={{ color: C.muted }}>{scopeOpen ? "▾" : "▸"}</span>
        </div>
        {scopeOpen && (
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10 }}>
            <div style={{ flex: 2, minWidth: 260 }}>
              <textarea
                defaultValue={(sheet.scope_doc?.scope || []).join("\n")}
                key={`scope_${sheet.id}_${(sheet.scope_doc?.scope || []).length}`}
                onBlur={e => saveScopeDoc({ scope: e.target.value.split("\n").map(s => s.replace(/^\s*[•\-*]\s*/, "").trim()).filter(Boolean) })}
                placeholder="One scope line per row — pushed from the quote on convert; edit freely."
                rows={Math.max(3, (sheet.scope_doc?.scope || []).length + 1)}
                style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.surface, width: "100%", resize: "vertical", lineHeight: 1.5 }} />
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 4 }}>Notes</div>
              <textarea
                defaultValue={(sheet.scope_doc?.notes || []).join("\n")}
                key={`notes_${sheet.id}_${(sheet.scope_doc?.notes || []).length}`}
                onBlur={e => saveScopeDoc({ notes: e.target.value.split("\n").map(s => s.trim()).filter(Boolean) })}
                placeholder="Job notes — gotchas, access, client asks…"
                rows={Math.max(3, (sheet.scope_doc?.notes || []).length + 1)}
                style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.surface, width: "100%", resize: "vertical", lineHeight: 1.5 }} />
            </div>
          </div>
        )}

        {/* Jobber context: photos from job/request notes + the human notes */}
        {jobberCtx?.media?.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 6 }}>Photos from Jobber notes ({jobberCtx.media.length}) — tap to open full size</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {jobberCtx.media.slice(0, 16).map((m, i) => (
                <a key={i} href={m.url} target="_blank" rel="noreferrer" title={`${m.fileName || "photo"} (${m.source})`}>
                  <img src={m.thumb} alt={m.fileName || "job photo"} style={{ width: 76, height: 76, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}` }} />
                </a>
              ))}
            </div>
          </div>
        )}
        {jobberCtx?.notes?.length > 0 && (
          <div style={{ marginTop: 10 }}>
            <span onClick={() => setShowJobberNotes(v => !v)} style={{ fontSize: 12, color: C.blue, cursor: "pointer" }}>
              {showJobberNotes ? "▾" : "▸"} Jobber notes ({jobberCtx.notes.length})
            </span>
            {showJobberNotes && jobberCtx.notes.map((n, i) => (
              <div key={i} style={{ fontSize: 12, color: C.text, borderLeft: `2px solid ${C.border}`, padding: "4px 8px", margin: "6px 0", whiteSpace: "pre-wrap" }}>
                <span style={{ color: C.muted }}>{n.createdAt ? new Date(n.createdAt).toLocaleDateString("en-CA") : ""} · {n.source}{n.author ? ` · ${n.author}` : ""}{n.files ? ` · 📎${n.files}` : ""} — </span>{n.message}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Takeoff assistant — shorthand adds, line edits, book/CEC/web lookups */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: "10px 14px", marginBottom: 12 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: C.lime, marginBottom: 6 }}>💬 Takeoff assistant</div>
        {chatMsgs.some(m => m.lane !== "pad") && (
          <div style={{ maxHeight: 280, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
            {chatMsgs.filter(m => m.lane !== "pad").map((m, i) => (
              <div key={i} style={{
                alignSelf: m.r === "user" ? "flex-end" : "flex-start", maxWidth: "88%",
                background: m.r === "user" ? "rgba(165,206,58,.12)" : C.surface,
                border: `1px solid ${m.r === "user" ? "rgba(165,206,58,.35)" : C.border}`,
                borderRadius: 10, padding: "7px 10px", fontSize: 12.5, color: C.text, whiteSpace: "pre-wrap",
              }}>
                {String(m.t || "").replace(/\*\*/g, "")}
                {m.ops ? <div style={{ fontSize: 10.5, color: C.lime, marginTop: 3 }}>✓ {m.ops} change{m.ops === 1 ? "" : "s"} applied to the list</div> : null}
              </div>
            ))}
            {chatBusy && <div style={{ alignSelf: "flex-start", color: C.muted, fontSize: 12 }}>thinking…</div>}
            <div ref={chatEndRef} />
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <input value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") sendChat(); }}
            placeholder={dictating === "top" ? "Listening… tap ⏹ when you're done, then Send" : `e.g. "need wet connectors for the #6, 2 dry extra" · "what's code for plug spacing" · "set the ATS to ordered"`}
            style={{ ...inputStyle, flex: 1, border: `1px solid ${dictating === "top" ? C.red : C.border}`, background: C.surface }} disabled={chatBusy} />
          {micBtn("top", () => chatInput, setChatInput)}
          <button style={{ ...btn(true), opacity: chatBusy || !chatInput.trim() ? .5 : 1 }} disabled={chatBusy || !chatInput.trim()} onClick={sendChat}>{chatBusy ? "…" : "Send"}</button>
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
          Adds price from the book ({sheet.preferred_supplier ? <b style={{ color: C.lime }}>{sheet.preferred_supplier}</b> : "no supplier chosen"} first, others flagged), falls back to a web part-number search, and answers CEC code questions with clause numbers.
        </div>
      </div>

      {/* toolbar */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
        <button style={btn()} onClick={addColumn}>+ Column</button>
        <button style={btn()} onClick={async () => setMsg(await copyText(buildTakeoffText(sheet, itemsRef.current, "all"), "Whole sheet copied ✓ — scope, notes, materials and work pad"))}
          title="Copy the whole sheet as plain text — scope, notes, the takeoff lines and the work pad (no costs)">📋 Copy all</button>
        <button style={{ ...btn(), ...(textView ? { borderColor: C.lime, color: C.lime } : {}) }} onClick={() => setTextView(v => !v)}
          title="Show the whole sheet as selectable text so you can highlight and copy any part of it">🔤 Text view</button>
        <span style={{ width: 1, height: 22, background: C.border }} />
        <select value={repriceSupplier} onChange={e => setRepriceSupplier(e.target.value)} style={{ ...inputStyle, width: 170, border: `1px solid ${C.border}`, background: C.card }}>
          <option value="">Price from supplier…</option>
          {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button style={btn()} disabled={!repriceSupplier || repriceBusy} onClick={repriceNow}>
          {repriceBusy ? "Matching…" : `Match ${selItems.length ? selItems.length + " selected" : "all"}`}
        </button>
        <span style={{ flex: 1 }} />
        {/* Autosave state. The dot is the at-a-glance signal: green = it's on
            disk, amber = it isn't yet. Sits next to Save now so both the
            question and the answer are in the same place. */}
        <span title={dirty ? "Edits not written yet — they save ~1.5s after you stop typing, or hit Save now."
                           : lastSavedAt ? `Last written to the database at ${fmtClock(lastSavedAt)}` : "No changes yet this session"}
          style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, whiteSpace: "nowrap", fontWeight: 700,
                   color: (saving || dirty) ? C.amber : C.green }}>
          <span style={{ width: 9, height: 9, borderRadius: "50%", flexShrink: 0,
                         background: (saving || dirty) ? C.amber : C.green,
                         boxShadow: `0 0 6px ${(saving || dirty) ? C.amber : C.green}`, transition: "background .25s, box-shadow .25s" }} />
          {saving ? "Saving…" : dirty ? "Unsaved changes" : lastSavedAt ? `Saved ${fmtClock(lastSavedAt)}` : "Saved"}
        </span>
        {dirty && <button style={btn(true)} disabled={saving} onClick={() => flushSave()}>{saving ? "Saving…" : "Save now"}</button>}
      </div>

      {/* filter / search bar — blue is the only accent unclaimed on this grid
          (lime = header/selection, amber = invoice review, green = stock). */}
      <div style={{
        display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10,
        background: filterOn ? "rgba(96,165,250,.08)" : C.card,
        border: `1px solid ${filterOn ? "rgba(96,165,250,.45)" : C.border}`,
        borderRadius: 10, padding: "8px 10px",
      }}>
        <input value={q} onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === "Escape") clearFilters(); }}
          placeholder="🔎 Search material, part #, notes…"
          title="Loose match — 'recepticle' finds receptacles, 'plug' finds outlets. Sizes and ratings (14/2, 15a, #6) are matched exactly, never fuzzily."
          style={{ ...inputStyle, width: 250, border: `1px solid ${C.border}`, background: C.card }} />
        <select value={fStatus} onChange={e => setFStatus(e.target.value)}
          style={{ ...inputStyle, width: 168, border: `1px solid ${C.border}`, background: C.card }}>
          <option value="">All statuses</option>
          {/* `|| s.key === fStatus` keeps the select from rendering blank when the
              tech re-statuses every row out of the filter they're standing in. */}
          {STATUSES.filter(s => facets.st[s.key] || s.key === fStatus).map(s => (
            <option key={s.key} value={s.key}>{s.label} ({facets.st[s.key] || 0})</option>
          ))}
        </select>
        <select value={fSupplier} onChange={e => setFSupplier(e.target.value)}
          style={{ ...inputStyle, width: 180, border: `1px solid ${C.border}`, background: C.card }}>
          <option value="">All suppliers</option>
          {facets.sup.map(([key, v]) => <option key={key} value={key}>{v.label} ({v.n})</option>)}
          {facets.noSup > 0 && <option value={NO_SUPPLIER}>(no supplier) ({facets.noSup})</option>}
        </select>
        {facets.colors.length > 0 && (
          <span style={{ display: "flex", gap: 4, alignItems: "center" }} title="Filter by highlight colour">
            {facets.colors.map(hex => (
              <span key={hex} onClick={() => setFColor(c => (c === hex ? "" : hex))}
                style={{ width: 16, height: 16, borderRadius: 4, cursor: "pointer", background: hex, border: fColor === hex ? `2px solid ${C.text}` : `1px solid ${C.border}` }} />
            ))}
            <span onClick={() => setFColor(c => (c === ANY_COLOR ? "" : ANY_COLOR))}
              style={{ ...chip(fColor === ANY_COLOR ? C.lime : C.muted), cursor: "pointer" }}>any</span>
          </span>
        )}
        <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 5, cursor: "pointer", whiteSpace: "nowrap" }}
          title="Tint each row by its status. 'Needed' stays plain on purpose — the plain rows are the ones still outstanding.">
          <input type="checkbox" checked={tintByStatus}
            onChange={e => {
              setTintByStatus(e.target.checked);
              try { localStorage.setItem("takeoff_tint", e.target.checked ? "1" : "0"); } catch { /* private mode */ }
            }} />
          🎨 Tint by status
        </label>
        <span style={{ flex: 1 }} />
        {filterOn && (
          <>
            <span style={{ ...chip(C.blue), cursor: "pointer" }} onClick={clearFilters}>
              {visibleSelKeys.length} of {bodyLineCount} shown · clear ✕
            </span>
            {visibleSelKeys.length > 0 && (
              <button style={btn()} onClick={() => setSelected(new Set(visibleSelKeys))}>Select all {visibleSelKeys.length}</button>
            )}
          </>
        )}
      </div>

      {/* apply-to-filtered — the literal "filter, then change them all" loop */}
      {filterOn && visibleSelKeys.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginBottom: 10, padding: "6px 10px", fontSize: 12, color: C.muted, background: "rgba(96,165,250,.05)", border: `1px dashed rgba(96,165,250,.35)`, borderRadius: 10 }}>
          <span style={{ fontWeight: 700, color: C.blue }}>Apply to all {visibleSelKeys.length} shown:</span>
          <select value="" onChange={e => { const v = e.target.value; if (v) stageBulk("filtered", "status", v, statusOf(v).label); e.target.value = ""; }}
            style={{ ...inputStyle, width: 150, border: `1px solid ${C.border}`, background: C.card }}>
            <option value="">Set status…</option>
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value="" onChange={e => { const v = e.target.value; if (v) stageBulk("filtered", "location", v, v); e.target.value = ""; }}
            style={{ ...inputStyle, width: 150, border: `1px solid ${C.border}`, background: C.card }}>
            <option value="">Set location…</option>
            {LOCATION_SUGGESTIONS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          <select value="" onChange={e => { const v = e.target.value; if (v) stageBulk("filtered", "supplier", v, v); e.target.value = ""; }}
            style={{ ...inputStyle, width: 150, border: `1px solid ${C.border}`, background: C.card }}>
            <option value="">Set supplier…</option>
            {suppliers.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          {swatchStrip(visibleSelKeys, "Highlight all shown lines")}
        </div>
      )}

      {/* staged confirm for an apply-to-all-SHOWN edit — beside the filter bar
          that scoped it. A selection-scope confirm renders in the sticky zone. */}
      {pendingBulk && pendingBulk.scope === "filtered" && pendingBulkStrip()}

      {err && data && <div style={{ fontSize: 12.5, color: C.red, marginBottom: 8 }}>{err} <span style={{ color: C.muted, cursor: "pointer", textDecoration: "underline" }} onClick={() => { setErr(null); load(); }}>reload</span></div>}
      {branchesError && <div style={{ fontSize: 12.5, color: C.amber, marginBottom: 8 }}>Supplier list didn't load: {branchesError} — the order and quote pickers will be empty until you reload.</div>}

      {/* Sticky action zone: the selection bar and everything it launches —
          the staged confirms and the result banner with its undo chips —
          travel with the scroll. A tech ticking work-pad lines at the bottom of
          a long sheet sees "Yes — 5 lines" beside the button they pressed, not
          1500px above. Rendered only when it has something to show, so there is
          never an empty stuck box. */}
      {(selItems.length > 0 || pendingPad || (pendingBulk && pendingBulk.scope === "selection") || repriceMsg || msg || bulkUndo || padUndo) && (
      <div className="tk-selbar" style={{ background: C.bg, paddingTop: 4 }}>
      {selItems.length > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", background: "#1b2417", border: `1px solid rgba(165,206,58,.35)`, borderRadius: 10, padding: "8px 10px", marginBottom: 10 }}>
          <span style={{ fontSize: 12.5, color: C.lime, fontWeight: 700 }}>
            {selItems.length} selected{selQuoteCount && selQuoteCount < selItems.length ? <span style={{ color: C.muted, fontWeight: 400 }}> ({selItems.length - selQuoteCount} on the work pad)</span> : !selQuoteCount ? <span style={{ color: C.muted, fontWeight: 400 }}> · work pad</span> : null}{filterOn ? <span style={{ color: C.blue, fontWeight: 400 }}> · filtered view</span> : null}
          </span>
          {/* Both flush first: selItems drops id-less new rows until the flush
              adopts their server ids, so an unflushed send would silently omit
              lines the tech just typed. */}
          <button style={btn(true)} onClick={async () => { await flushSave(); setOrdering("order"); }}>📦 Order these…</button>
          <button style={btn()} onClick={async () => { await flushSave(); setOrdering("rfq"); }}
            title="Ask several suppliers to price these lines — no PO, and nothing flips to Ordered">💬 Ask for a quote…</button>
          <button style={btn()} onClick={checkStock} title="Search the van / sea-can / shop stock list for these items">🔍 Check stock</button>
          <button style={btn()} onClick={openSavePackage} title="Save the selected lines as a reusable material package (add it to any takeoff later, auto-add it to matching quotes, or attach it to a quote template)">🧰 Save as package…</button>
          {selQuoteCount > 0 && (
            <button style={{ ...btn(), borderColor: C.lime + "88", color: C.lime }} onClick={stagePad}
              title="Copy the ticked quote lines down to the work pad as qty + description (you choose whether the quote's part numbers come along)">⬇ Add to work pad</button>
          )}
          <select value="" onChange={e => { const v = e.target.value; if (v) stageBulk("selection", "status", v, statusOf(v).label); e.target.value = ""; }} style={{ ...inputStyle, width: 150, border: `1px solid ${C.border}`, background: C.card }}>
            <option value="">Set status…</option>
            {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
          <select value="" onChange={e => { const v = e.target.value; if (v) stageBulk("selection", "location", v, v); e.target.value = ""; }} style={{ ...inputStyle, width: 150, border: `1px solid ${C.border}`, background: C.card }}>
            <option value="">Set location…</option>
            {LOCATION_SUGGESTIONS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
          {swatchStrip([...selected], "Highlight the selected lines")}
          <button style={btn(false, true)} onClick={removeSelected}>Delete</button>
          <button style={btn()} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}
      {pendingBulk && pendingBulk.scope === "selection" && pendingBulkStrip()}
      {/* staged "send down to the pad" — off by default for part numbers: the
          tech said the quote's are usually wrong, so only what they choose is truth */}
      {pendingPad && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 10, padding: "9px 12px", background: "rgba(165,206,58,.10)", border: `1px solid rgba(165,206,58,.5)`, borderRadius: 10 }}>
          <span style={{ fontSize: 12.5, color: C.text, fontWeight: 700 }}>Send {pendingPad.count} line{pendingPad.count === 1 ? "" : "s"} down to the work pad?</span>
          <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
            <input type="checkbox" checked={pendingPad.carry} onChange={e => setPendingPad(p => ({ ...p, carry: e.target.checked }))} />
            also carry the quote's part # / supplier / cost <span style={{ color: C.muted }}>(unticked = qty + description only)</span>
          </label>
          <button style={btn(true)} onClick={() => addToPad(pendingPad.keys, pendingPad.carry)}>Yes — add {pendingPad.count}</button>
          <button style={btn()} onClick={() => setPendingPad(null)}>Cancel</button>
        </div>
      )}
      {(repriceMsg || msg || bulkUndo || padUndo) && (
        <div style={{ fontSize: 12.5, color: C.lime, marginBottom: 8, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span>{repriceMsg || msg}</span>
          {bulkUndo && <span style={{ ...chip(C.amber), cursor: "pointer" }} onClick={undoBulk}>↩ Undo {bulkUndo.label}</span>}
          {padUndo && <span style={{ ...chip(C.amber), cursor: "pointer" }} onClick={undoPadImport}>↩ Remove the {padUndo.n} imported line{padUndo.n === 1 ? "" : "s"}</span>}
        </div>
      )}
      </div>
      )}

      {/* text view — inputs can't be drag-selected across rows, so this is the
          copyable form of the whole sheet: select any part with the cursor, or
          copy it all in one tap */}
      {textView && (
        <div style={{ background: C.card, border: `1px solid ${C.lime}66`, borderRadius: 12, padding: "10px 14px", marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 16, color: C.lime, flex: 1, minWidth: 200 }}>🔤 Text view — drag to select any part, or copy it all</div>
            <button style={btn(true)} onClick={async () => setMsg(await copyText(buildTakeoffText(sheet, itemsRef.current, "all"), "Whole sheet copied ✓"))}>📋 Copy all</button>
            <button style={btn()} onClick={async () => setMsg(await copyText(buildTakeoffText(sheet, itemsRef.current, "pad"), "Work pad copied ✓"))}>📋 Copy work pad only</button>
            <button style={btn()} onClick={() => setTextView(false)}>✕ Close</button>
          </div>
          <pre className="tk-text" style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12.5, lineHeight: 1.55, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", maxHeight: 520, overflowY: "auto" }}>
            {buildTakeoffText(sheet, items, "all")}
          </pre>
        </div>
      )}

      {/* grid */}
      <div data-rsel="takeoff" style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 14 }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12.5, minWidth: 1240, width: "100%" }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", background: "#11141a" }}>
                {[
                  ["", 24], ["", 26], ["Status", 120], ["Qty", 54], ["Unit", 46],
                  ["Material / Device", 260], ["Purpose", 150], ["Part #", 95], ["Supplier", 125],
                  ["Cost/unit", 78], [`Our total (${tkBasisLabel(taxBasis)})`, 96], ["Markup %", 68], ["PST %", 52],
                  ["Client each", 84], ["Client total", 88],
                  ["Location", 110], ["Notes", 130], ["Actual $", 76],
                ].map(([label, w], hi) => (
                  <th key={hi} style={{ padding: "8px 6px", fontWeight: 600, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}`, minWidth: w }}>
                    {hi === 1
                      ? <input type="checkbox"
                          checked={visibleSelKeys.length > 0 && visibleSelKeys.every(k => selected.has(k))}
                          onChange={e => setSelected(e.target.checked ? new Set(visibleSelKeys) : new Set())}
                          title={filterOn ? `Select all ${visibleSelKeys.length} shown` : "Select all"} />
                      : label}
                  </th>
                ))}
                {customCols.map(col => (
                  <th key={col.key} style={{ padding: "8px 6px", fontWeight: 600, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}`, minWidth: col.width || 110 }}>
                    {col.label}<span onClick={() => removeColumn(col.key)} title="Remove column" style={{ cursor: "pointer", color: C.muted, marginLeft: 6 }}>×</span>
                  </th>
                ))}
                <th style={{ width: 34, borderBottom: `1px solid ${C.border}` }} />
              </tr>
            </thead>
            <tbody>
              {gridRowCount === 0 && <tr><td colSpan={colSpanAll} style={{ padding: 18, color: C.muted, textAlign: "center" }}>Empty — add rows below, or pull the quote's materials with “⟳ Re-pull quote”.</td></tr>}
              {items.length > 0 && filterOn && visibleSelKeys.length === 0 && (
                <tr><td colSpan={colSpanAll} style={{ padding: 18, color: C.muted, textAlign: "center" }}>
                  {qTokens.length ? `No lines match “${q}”` : "No lines match this filter"} — <span style={{ color: C.lime, cursor: "pointer" }} onClick={clearFilters}>clear</span>
                </td></tr>
              )}
              {/* DO NOT map a filtered array here. Every row handler below takes
                  the items[] index (setItem, removeRow, moveRow, applyPick,
                  numCell) and flushSave records its dirty keys against that same
                  index — mapping a filtered array drops edits from the save
                  payload with no error while the header still reads "saved".
                  Filtering is display:none only. Rows also stay MOUNTED, which
                  is what preserves in-flight typing in the uncontrolled numeric
                  cells: hiding a focused input blurs it and commits instead. */}
              {items.map((it, i) => {
                // Work-pad rows render in their own grid below. Returning null
                // keeps `i` as the TRUE items[] index (the invariant above is
                // about filtering the array, not about skipping in the map).
                if (laneOf(it) === "pad") return null;
                const k = rowKey(it, i);
                const hl = hlOf(it);
                return (
                <Fragment key={k}>
                  <tr
                    style={{
                      display: visibleKeys.has(k) ? undefined : "none",
                      borderBottom: "1px solid #1e2328",
                      background: it.is_header ? "rgba(165,206,58,.06)"
                        : selected.has(k) ? "rgba(165,206,58,.04)"
                        : hl ? hexA(hl, 0.10)
                        // "needed" gets the strongest wash of any status: it's
                        // the work that hasn't been sourced yet, so it should be
                        // the first thing the eye lands on.
                        : tintByStatus ? statusOf(it.status).color + ((it.status || "needed") === "needed" ? "1f" : "12")
                        : "none",
                    }}
                    onDragOver={e => e.preventDefault()}
                    // preventDefault is NOT optional: without it a dropped FILE
                    // falls through to the browser, which navigates the tab to
                    // it and takes any unsaved edits with it. Same bug as the
                    // quote's materials table (fixed in a99c2ebf).
                    onDrop={e => {
                      e.preventDefault();
                      if (filterOn) return;
                      if (dragFromRef.current != null) moveRow(dragFromRef.current, i);
                      dragFromRef.current = null;
                    }}
                  >
                    {/* Status stripe lives on the <td>: the table is borderCollapse
                        so a border on the <tr> would not render. Reordering is
                        disabled under a filter — moveRow renumbers EVERY row and
                        dropping onto a visible neighbour lands between hidden ones. */}
                    <td draggable={!filterOn} onDragStart={e => { dragFromRef.current = i; e.dataTransfer.effectAllowed = "move"; }}
                      // Cleared however the drag ends: a stale index would make
                      // the work pad refuse the next FILE dropped on it.
                      onDragEnd={() => { dragFromRef.current = null; }}
                      style={{ padding: "2px 4px", cursor: filterOn ? "default" : "grab", color: C.muted, userSelect: "none", textAlign: "center", borderLeft: `4px solid ${it.is_header ? C.lime : statusOf(it.status).color}` }}
                      title={filterOn ? "Clear the filter to reorder rows" : "Drag to reorder"}>⠿</td>
                    <td style={{ padding: "2px 4px", textAlign: "center", position: "relative" }}>
                      {!it.is_header && (
                        <span onClick={() => setHlOpen(cur => (cur === k ? null : k))} title={hl ? "Change / clear this line's highlight" : "Highlight this line"}
                          style={{ position: "absolute", left: 1, top: "50%", transform: "translateY(-50%)", width: 6, height: 20, borderRadius: 3, cursor: "pointer", background: hl || "transparent", border: hl ? "none" : `1px dashed ${C.border}` }} />
                      )}
                      {!it.is_header && <input type="checkbox" checked={selected.has(k)} onChange={() => setSelected(prev => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; })} />}
                      {hlOpen === k && (
                        <div style={{ position: "absolute", zIndex: 70, top: "100%", left: 0, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 6, boxShadow: "0 8px 24px rgba(0,0,0,.55)" }}>
                          {swatchStrip([k], "Highlight this line")}
                        </div>
                      )}
                    </td>
                    {it.is_header ? (
                      <td colSpan={colSpanAll - 3} {...rc("grid", i, null, { padding: "2px 3px" })}>
                        <input value={it.description || ""} onChange={e => setItem(i, { description: e.target.value })} placeholder="Section header…"
                          style={{ ...inputStyle, fontWeight: 800, color: C.lime, fontSize: 13 }} />
                      </td>
                    ) : (
                      <>
                        {statusCell(it, i, k, "grid")}
                        <td {...rc("grid", i, 1, { padding: "2px 3px" })}>{numCell(it, i, "qty")}</td>
                        <td {...rc("grid", i, 2, { padding: "2px 3px" })}><input value={it.unit || ""} onChange={e => setItem(i, { unit: e.target.value })} style={inputStyle} /></td>
                        <td {...rc("grid", i, 3, { padding: "2px 3px", whiteSpace: "nowrap" })}>
                          <div style={{ display: "flex", gap: 4, alignItems: "center", position: "relative" }}>
                            <input
                              style={{ ...inputStyle, minWidth: 200 }} value={it.description || ""}
                              // Editing un-pins the book product id — a stale id would
                              // let a 💾 save overwrite a DIFFERENT product's price.
                              onChange={e => {
                                setItem(i, { description: e.target.value, ...(it.master_product_id ? { master_product_id: null } : {}) });
                                if (suggestable(it)) setInline({ idx: i, term: e.target.value });
                              }}
                              // Pin the row while it is being edited so a filter
                              // can't pull it out from under the cursor mid-type.
                              onFocus={() => { setStickyKey(k); if (suggestable(it) && String(it.description || "").trim().length >= 3) setInline({ idx: i, term: it.description }); }}
                              onBlur={() => { setStickyKey(cur => (cur === k ? null : cur)); setTimeout(() => setInline(cur => (cur && cur.idx === i ? null : cur)), 200); }}
                              onKeyDown={e => { if (e.key === "Escape") setInline(null); }}
                              placeholder="Item description — type to search the book"
                            />
                            {inline?.idx === i && (inlineResults || inlineBusy || inlineError) && (
                              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 60, background: C.surface, border: `1px solid ${C.lime}66`, borderRadius: 8, minWidth: 400, maxWidth: 560, maxHeight: 260, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,.55)" }}>
                                {inlineBusy && <div style={{ padding: 8, fontSize: 11, color: C.muted }}>Searching the book…</div>}
                                {!inlineBusy && inlineError && <div style={{ padding: 8, fontSize: 11, color: "#fca5a5" }}>{inlineError}</div>}
                                {!inlineBusy && !inlineError && inlineResults?.length === 0 && <div style={{ padding: 8, fontSize: 11, color: C.muted }}>No book match — set your cost, then 💾 to add it.</div>}
                                {!inlineBusy && (inlineResults || []).map(({ p, pr }, k) => (
                                  <button key={k} onMouseDown={e => { e.preventDefault(); applyPick(i, p, pr); }}
                                    style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: `1px solid ${C.border}`, background: "transparent", color: C.text, padding: "7px 10px", fontSize: 12, fontFamily: "inherit", cursor: "pointer", whiteSpace: "normal" }}>
                                    <span style={{ fontWeight: 700, color: C.lime }}>{money(pr.unit_price)}</span>
                                    <span> {pr.supplier_name}</span>
                                    <span style={{ color: C.muted, fontSize: 11 }}> · {(pr.date_recorded || "").slice(0, 10)}</span>
                                    <div style={{ fontSize: 11, color: C.muted }}>{pr.display_description || pr.supplier_description || p.description}{p.sku ? ` · ${p.sku}` : ""}</div>
                                  </button>
                                ))}
                              </div>
                            )}
                            <button onClick={() => setSearchIdx(i)} style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer", padding: "2px 5px", flexShrink: 0 }} title="Search the price book for this item">🔍</button>
                            {it.id && padSrcIds.has(it.id) && <span title="Already on the work pad" style={{ ...chip(C.lime), padding: "0 5px", fontSize: 10.5, flexShrink: 0 }}>↓ pad</span>}
                          </div>
                        </td>
                        {/* Purpose — where it goes / what it feeds. Kept OUT of the
                            material name so the name stays a pure product string:
                            the price matcher scores every word in it, and the price
                            book is fed from it. */}
                        <td {...rc("grid", i, 4, { padding: "2px 3px" })}>
                          <input value={it.purpose || ""} onChange={e => setItem(i, { purpose: e.target.value })}
                            placeholder="where / why" style={{ ...inputStyle, color: C.muted, fontStyle: it.purpose ? "normal" : "italic" }}
                            title="Where this goes or what it feeds. Never part of the product name — the matcher and the price book only read the name." />
                        </td>
                        <td {...rc("grid", i, 5, { padding: "2px 3px" })}><input value={it.part_number || ""} onChange={e => setItem(i, { part_number: e.target.value })} style={inputStyle} /></td>
                        <td {...rc("grid", i, 6, { padding: "2px 3px", whiteSpace: "nowrap" })}>
                          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <input list="takeoff-suppliers" value={it.supplier || ""} onChange={e => setItem(i, { supplier: e.target.value })}
                              style={{ ...inputStyle, minWidth: 90, ...(it.supplier_quoted && it.supplier && it.supplier !== it.supplier_quoted ? { color: C.amber } : {}) }}
                              title={it.supplier_quoted ? `Quoted from: ${it.supplier_quoted}` : ""} />
                            <button onClick={() => setSavePriceIdx(savePriceIdx === rowKey(it, i) ? null : rowKey(it, i))}
                              style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer", padding: "2px 4px", flexShrink: 0, fontSize: 11 }}
                              title="Save this price to the price book as the default for future quotes">💾</button>
                          </div>
                        </td>
                        <td {...rc("grid", i, 7, { padding: "2px 3px" })}>{numCell(it, i, "unit_cost")}</td>
                        <td {...rc("grid", i, 8, { padding: "5px 6px", textAlign: "right", color: C.muted, whiteSpace: "nowrap" })}
                          title={`${Number(it.qty) || 0} × ${money(it.unit_cost)} = ${money(ourExTotal(it))} ex tax · with PST ${money(ourTotalOf(it))}`}>
                          {ourShownTotal(it, taxBasis) ? money(ourShownTotal(it, taxBasis)) : "—"}
                        </td>
                        <td {...rc("grid", i, 9, { padding: "2px 3px" })}>{numCell(it, i, "markup_pct")}</td>
                        <td {...rc("grid", i, 10, { padding: "2px 3px" })}>{numCell(it, i, "pst_pct")}</td>
                        <td {...rc("grid", i, 11, { padding: "5px 6px", textAlign: "right", color: C.muted, whiteSpace: "nowrap" })}>{clientEach(it) ? money(clientEach(it)) : "—"}</td>
                        <td {...rc("grid", i, 12, { padding: "5px 6px", textAlign: "right", color: C.text, fontWeight: 600, whiteSpace: "nowrap" })}>{clientTotalOf(it) ? money(clientTotalOf(it)) : "—"}</td>
                        <td {...rc("grid", i, 13, { padding: "2px 3px" })}><input list="takeoff-locations" value={it.location || ""} onChange={e => setItem(i, { location: e.target.value })} style={inputStyle} /></td>
                        <td {...rc("grid", i, 14, { padding: "2px 3px" })}><input value={it.notes || ""} onChange={e => setItem(i, { notes: e.target.value })} style={inputStyle} /></td>
                        <td {...rc("grid", i, 15, { padding: "5px 6px", textAlign: "right", whiteSpace: "nowrap", color: it.actual_total != null ? C.teal : C.muted })}
                          title={it.actual_matched ? `Auto-linked from supplier invoice (${it.actual_matched})` : ""}>
                          {it.actual_total != null ? money(it.actual_total)
                            : it.review_suggestion ? (
                              <span onClick={() => setReviewOpen(reviewOpen === it.id ? null : it.id)} title="Possible invoice-line match — click to review"
                                style={{ ...chip(C.amber), cursor: "pointer" }}>$?</span>
                            ) : "—"}
                        </td>
                        {customCols.map((col, ci) => (
                          <td key={col.key} {...rc("grid", i, 16 + ci, { padding: "2px 3px" })}>
                            <input value={(it.custom || {})[col.key] ?? ""} onChange={e => setCustom(i, col.key, e.target.value)} style={inputStyle} />
                          </td>
                        ))}
                      </>
                    )}
                    <td style={{ padding: "2px 4px", textAlign: "center" }}>
                      <span onClick={() => removeRow(i)} title="Remove line" style={{ cursor: "pointer", color: C.muted, fontSize: 15 }}>×</span>
                    </td>
                  </tr>
                  {/* Sub-rows are gated on the parent's visibility so a price
                      panel / stock strip / review strip can never float
                      detached from a hidden row. */}
                  {visibleKeys.has(k) && savePriceIdx === k && !it.is_header && (
                    <tr>
                      <td colSpan={colSpanAll} style={{ padding: "4px 8px 8px" }}>
                        <SavePricePanel item={it} suppliers={suppliers}
                          onSaved={(saved) => {
                            setItem(i, { master_product_id: saved.master_product_id || it.master_product_id, supplier: saved.supplier_name || it.supplier });
                            setSavePriceIdx(null);
                            setMsg(saved.created_new_product ? "Saved as a NEW price book item ✓" : "Price saved to the book ✓");
                          }}
                          onClose={() => setSavePriceIdx(null)} />
                      </td>
                    </tr>
                  )}
                  {stockStrip(it, k, colSpanAll)}
                  {visibleKeys.has(k) && it.id && reviewOpen === it.id && it.review_suggestion && (
                    <tr style={{ background: "rgba(245,158,11,.08)" }}>
                      <td colSpan={colSpanAll} style={{ padding: "6px 12px", fontSize: 12, color: C.amber }}>
                        Invoice line ({it.review_suggestion.supplier || "?"}{it.review_suggestion.invoice_number ? ` #${it.review_suggestion.invoice_number}` : ""}):{" "}
                        <b>{it.review_suggestion.supplier_description || it.review_suggestion.supplier_sku || "?"}</b> @ {money(it.review_suggestion.unit_price)}/unit
                        <span style={{ color: C.muted }}> — {Math.round((it.review_suggestion.score || 0) * 100)}% match. Use as this line's actual cost?</span>
                        <button style={{ ...btn(), padding: "2px 8px", marginLeft: 8, fontSize: 11.5, borderColor: C.green, color: C.green }} onClick={() => resolveRev(it, true)}>Yes — apply</button>
                        <button style={{ ...btn(), padding: "2px 8px", marginLeft: 6, fontSize: 11.5 }} onClick={() => resolveRev(it, false)}>No — dismiss</button>
                      </td>
                    </tr>
                  )}
                </Fragment>
                );
              })}
            </tbody>
            <tfoot>
              {/* Ticked lines first when there are any, but the whole sheet
                  ALWAYS stays on the row below it — a partial total that reads
                  like the sheet total is how a job gets ordered short. */}
              {selLines > 0 && (
                <tr style={{ background: "rgba(165,206,58,.10)", color: C.text, fontWeight: 700 }}>
                  <td colSpan={colSpanAll} style={{ padding: "8px 10px", textAlign: "right", color: C.muted }}>
                    <span style={{ color: C.lime }}>✓ Selected</span>
                    <span style={{ fontWeight: 400 }}> · {selLines} line{selLines === 1 ? "" : "s"}</span>
                    {` — our cost (${tkBasisLabel(taxBasis)}) `}<span style={{ color: C.text }}>{money(selTotals.shown)}</span>
                    {" · "}client <span style={{ color: C.lime }}>{money(selTotals.client)}</span>
                    {" · "}profit <span style={{ color: selTotals.client - selTotals.our >= 0 ? C.green : C.red }}>{money(selTotals.client - selTotals.our)}</span>
                    {selTotals.client > 0 ? <span style={{ color: C.muted, fontWeight: 400 }}> ({marginPctOf(selTotals).toFixed(0)}% margin{basisNoteOf(selTotals)})</span> : null}
                    {selTotals.actual > 0 ? <>{" · "}actual <span style={{ color: C.teal }}>{money(selTotals.actual)}</span></> : null}
                  </td>
                </tr>
              )}
              <tr style={{ background: "#11141a", color: C.text, fontWeight: selLines > 0 ? 500 : 700 }}>
                <td colSpan={colSpanAll} style={{ padding: "8px 10px", color: C.muted }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", justifyContent: "space-between" }}>
                    {taxToggles}
                    <span style={{ textAlign: "right" }}>
                      {selLines > 0 ? (filterOn ? `Whole sheet (filtered — ${visibleSelKeys.length} of ${bodyLineCount} lines)` : `Whole sheet · ${bodyLineCount} lines`)
                        : (filterOn ? `Totals (filtered — ${visibleSelKeys.length} of ${bodyLineCount} lines)` : "Totals")}
                      {` — our cost (${tkBasisLabel(taxBasis)}) `}
                      <span style={{ color: C.text }}>{money(totals.shown)}</span>
                      {" · "}client <span style={{ color: C.lime }}>{money(totals.client)}</span>
                      {" · "}profit <span style={{ color: totals.client - totals.our >= 0 ? C.green : C.red }}>{money(totals.client - totals.our)}</span>
                      {totals.client > 0 ? <span style={{ color: C.muted, fontWeight: 400 }}> ({marginPctOf(totals).toFixed(0)}% margin{basisNoteOf(totals)})</span> : null}
                      {" · "}actual so far <span style={{ color: totals.actual > totals.ex && totals.ex > 0 ? C.red : C.teal }}>{money(totals.actual)}</span>
                      {/* Invoice line totals are EX-TAX, so they are measured against the
                          ex-tax quoted cost. Comparing them to the PST-in figure (as this
                          did before) made every job look ~7% under budget. */}
                      {totals.ex > 0 && totals.actual > 0 ? <span style={{ color: C.muted, fontWeight: 400 }} title="Invoice line totals are ex tax, so this compares them to the ex-tax quoted cost">
                        {" "}({totals.actual > totals.ex ? "+" : ""}{(((totals.actual - totals.ex) / totals.ex) * 100).toFixed(0)}% vs quoted ex tax)
                      </span> : null}
                    </span>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div style={{ padding: "8px 10px", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => addRow(false)} style={btn()}>+ Add material line</button>
          <button onClick={() => addRow(true)} style={btn()}>+ Section header</button>
          <button onClick={() => setPkgAdd(true)} style={btn()} title="Drop a saved material package (its own section header + lines) onto this takeoff">🧰 Add material package…</button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: C.muted }}>Copy a block: press on a cell, drag across and down, then <b>📋 Copy</b> or Ctrl+C.</span>
        </div>
      </div>

      {/* ── Work pad — the tech's own ordering list ──────────────────────────
          Qty + description, the way it goes to the counter. Nothing comes down
          from the quote grid unless the tech sends it; a part number is only
          ever one a human (or a supplier's own document) supplied. Same
          statuses and highlight colours as the grid so stages are marked off
          the same way. Drop target for a PDF / photo / text file, a pasted
          list, or text dragged from another window. */}
      <div {...padDrop} data-rsel="takeoff"
        style={{ background: C.surface, border: `2px ${padOver ? "dashed" : "solid"} ${padOver ? C.lime : C.border}`, borderRadius: 14, overflow: "hidden", marginBottom: 14, transition: "border-color .15s" }}>
        <div style={{ padding: "12px 14px 8px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, color: C.lime }}>🗒 Work pad — ordering list</div>
            <div style={{ fontSize: 11.5, color: C.muted }}>
              Qty + description, the way it goes to the counter. Nothing comes down from the quote unless you send it (tick lines above → <b>⬇ Add to work pad</b>). What you order from here is exactly what's here.
            </div>
          </div>
          <button style={btn()} onClick={() => setPadPasteOpen(v => !v)} title="Paste a parts list as text — the AI cleans it into lines">📥 Paste a list</button>
          <label style={{ ...btn(), display: "inline-flex", alignItems: "center" }} title="A supplier PDF, a photo of a handwritten list, or a text / CSV file — or just drop it anywhere on this card">
            📎 Drop / pick a file
            <input type="file" accept=".pdf,.csv,.tsv,.txt,.md,image/*,application/pdf,text/csv,text/plain" style={{ display: "none" }}
              onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) padImportFile(f); }} />
          </label>
          <button style={btn()} onClick={async () => setMsg(await copyText(buildTakeoffText(sheet, itemsRef.current, "pad"), "Work pad copied ✓ — paste it into a text or email"))}
            title="Copy the work pad as plain text — qty, description, part # and notes; supplier-ready">📋 Copy list</button>
          <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 5, cursor: "pointer", whiteSpace: "nowrap" }} title="Show supplier / cost / location / purpose columns on the pad">
            <input type="checkbox" checked={padMore} onChange={e => setPadMore(e.target.checked)} /> more columns
          </label>
        </div>
        {padOver && <div style={{ padding: "0 14px 8px", fontSize: 12.5, color: C.lime, fontWeight: 700 }}>Drop it — PDF, photo, CSV, text file or selected text.</div>}
        {padBusy && <div style={{ padding: "0 14px 8px", fontSize: 12.5, color: C.lime }}>⏳ {padBusy}</div>}

        {padPasteOpen && (
          <div style={{ padding: "0 14px 10px" }}>
            <textarea value={padPaste} onChange={e => setPadPaste(e.target.value)} rows={5} autoFocus
              placeholder={"Paste any parts list — one item per line is easiest, e.g.\n3 wet teck connectors for #6\n50m 14/2 NMD90\n2 x 20A GFCI receptacles [WI L110-1]"}
              style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card, width: "100%", resize: "vertical", lineHeight: 1.5, fontFamily: "ui-monospace, Menlo, Consolas, monospace" }} />
            <div style={{ display: "flex", gap: 8, marginTop: 6, alignItems: "center", flexWrap: "wrap" }}>
              <button style={{ ...btn(true), opacity: !padPaste.trim() || padBusy ? .5 : 1 }} disabled={!padPaste.trim() || !!padBusy}
                onClick={async () => { const t = padPaste; setPadPaste(""); setPadPasteOpen(false); await padImportText(t); }}>✨ Clean up &amp; add</button>
              <button style={btn()} onClick={() => { setPadPasteOpen(false); setPadPaste(""); }}>Cancel</button>
              <span style={{ fontSize: 11.5, color: C.muted }}>The AI tidies wording and pulls out quantities; it never adds part numbers or prices you didn't give it.</span>
            </div>
          </div>
        )}

        {/* the pad's own assistant — talk it in */}
        <div style={{ padding: "0 14px 10px" }}>
          {padChat.length > 0 && (
            <div style={{ maxHeight: 220, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
              {padChat.map((m, i) => (
                <div key={i} style={{
                  alignSelf: m.r === "user" ? "flex-end" : "flex-start", maxWidth: "88%",
                  background: m.r === "user" ? "rgba(165,206,58,.12)" : C.card,
                  border: `1px solid ${m.r === "user" ? "rgba(165,206,58,.35)" : C.border}`,
                  borderRadius: 10, padding: "7px 10px", fontSize: 12.5, color: C.text, whiteSpace: "pre-wrap",
                }}>
                  {String(m.t || "").replace(/\*\*/g, "")}
                  {m.ops ? <div style={{ fontSize: 10.5, color: C.lime, marginTop: 3 }}>✓ {m.ops} change{m.ops === 1 ? "" : "s"} on the work pad</div> : null}
                </div>
              ))}
              {padChatBusy && <div style={{ alignSelf: "flex-start", color: C.muted, fontSize: 12 }}>thinking…</div>}
              <div ref={padChatEndRef} />
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <input value={padChatInput} onChange={e => setPadChatInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") sendPadChat(); }}
              placeholder={dictating === "pad" ? "Listening… tap ⏹ when you're done, then Send" : `Talk it in: "3 wet connectors for the #6, 2 dry, a 100 amp meter base" · "mark the panel ordered"`}
              style={{ ...inputStyle, flex: 1, border: `1px solid ${dictating === "pad" ? C.red : C.border}`, background: C.card }} disabled={padChatBusy} />
            {micBtn("pad", () => padChatInput, setPadChatInput)}
            <button style={{ ...btn(true), opacity: padChatBusy || !padChatInput.trim() ? .5 : 1 }} disabled={padChatBusy || !padChatInput.trim()} onClick={sendPadChat}>{padChatBusy ? "…" : "Add"}</button>
          </div>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12.5, minWidth: padMore ? 1120 : 760, width: "100%" }}>
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", background: "#11141a" }}>
                {[
                  ["", 24], ["", 26], ["Status", 120], ["Qty", 54], ["Unit", 46],
                  ["Description", 300], ["Part # (only if you know it)", 140], ["Note for the supplier", 170],
                  ...(padMore ? [["Supplier", 125], ["Cost/unit", 78], ["Location", 110], ["Purpose", 130]] : []),
                ].map(([label, w], hi) => (
                  <th key={hi} style={{ padding: "8px 6px", fontWeight: 600, whiteSpace: "nowrap", borderBottom: `1px solid ${C.border}`, minWidth: w }}>
                    {hi === 1
                      ? <input type="checkbox"
                          checked={visiblePadKeys.length > 0 && visiblePadKeys.every(k => selected.has(k))}
                          onChange={e => setSelected(prev => { const n = new Set(prev); for (const k of visiblePadKeys) { if (e.target.checked) n.add(k); else n.delete(k); } return n; })}
                          title={filterOn ? `Select all ${visiblePadKeys.length} shown on the work pad` : "Select every work-pad line"} />
                      : label}
                  </th>
                ))}
                <th style={{ width: 34, borderBottom: `1px solid ${C.border}` }} />
              </tr>
            </thead>
            <tbody>
              {padRowCount === 0 && (
                <tr><td colSpan={colSpanPad} style={{ padding: 18, color: C.muted, textAlign: "center", lineHeight: 1.6 }}>
                  Nothing here yet — talk it in above, paste a list, drop a supplier PDF / photo, or tick lines in the takeoff and <b>⬇ Add to work pad</b>.
                </td></tr>
              )}
              {/* Same rule as the grid: the FULL items[] array is mapped and the
                  other lane returns null, so every handler keeps the true index. */}
              {items.map((it, i) => {
                if (laneOf(it) !== "pad") return null;
                const k = rowKey(it, i);
                const hl = hlOf(it);
                return (
                  <Fragment key={k}>
                  <tr
                    style={{ display: visibleKeys.has(k) ? undefined : "none", borderBottom: "1px solid #1e2328", background: rowBg(it, k, hl) }}
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => {
                      // A row reorder ends here and must NOT reach the card's
                      // file/text drop. A dropped file falls through to it.
                      if (dragFromRef.current == null) return;
                      e.preventDefault(); e.stopPropagation();
                      if (!filterOn) moveRow(dragFromRef.current, i);
                      dragFromRef.current = null;
                    }}
                  >
                    {handleCells(it, i, k, hl)}
                    {it.is_header ? (
                      <td colSpan={colSpanPad - 3} {...rc("pad", i, null, { padding: "2px 3px" })}>
                        <input value={it.description || ""} onChange={e => setItem(i, { description: e.target.value })} placeholder="Section header…"
                          style={{ ...inputStyle, fontWeight: 800, color: C.lime, fontSize: 13 }} />
                      </td>
                    ) : (
                      <>
                        {statusCell(it, i, k, "pad")}
                        <td {...rc("pad", i, 1, { padding: "2px 3px" })}>{numCell(it, i, "qty")}</td>
                        <td {...rc("pad", i, 2, { padding: "2px 3px" })}><input value={it.unit || ""} onChange={e => setItem(i, { unit: e.target.value })} style={inputStyle} /></td>
                        <td {...rc("pad", i, 3, { padding: "2px 3px" })}>
                          <input value={it.description || ""} onChange={e => setItem(i, { description: e.target.value })}
                            onFocus={() => setStickyKey(k)} onBlur={() => setStickyKey(cur => (cur === k ? null : cur))}
                            placeholder="What is it — type, size, rating, colour" style={{ ...inputStyle, minWidth: 220 }} />
                        </td>
                        <td {...rc("pad", i, 4, { padding: "2px 3px" })}>
                          <input value={it.part_number || ""} onChange={e => setItem(i, { part_number: e.target.value })}
                            placeholder="—" style={{ ...inputStyle, color: it.part_number ? C.text : C.muted }}
                            title="Only what you know for sure — the supplier reads this as an order code" />
                        </td>
                        <td {...rc("pad", i, 5, { padding: "2px 3px" })}>
                          <input value={it.notes || ""} onChange={e => setItem(i, { notes: e.target.value })}
                            placeholder="colour, use, 'same as last order'…" style={{ ...inputStyle, fontStyle: it.notes ? "normal" : "italic" }}
                            title="Goes on the order in brackets after the description" />
                        </td>
                        {padMore && (
                          <>
                            <td {...rc("pad", i, 6, { padding: "2px 3px" })}><input list="takeoff-suppliers" value={it.supplier || ""} onChange={e => setItem(i, { supplier: e.target.value })} style={inputStyle} /></td>
                            <td {...rc("pad", i, 7, { padding: "2px 3px" })}>{numCell(it, i, "unit_cost")}</td>
                            <td {...rc("pad", i, 8, { padding: "2px 3px" })}><input list="takeoff-locations" value={it.location || ""} onChange={e => setItem(i, { location: e.target.value })} style={inputStyle} /></td>
                            <td {...rc("pad", i, 9, { padding: "2px 3px" })}><input value={it.purpose || ""} onChange={e => setItem(i, { purpose: e.target.value })} placeholder="where / why" style={{ ...inputStyle, color: C.muted }} /></td>
                          </>
                        )}
                      </>
                    )}
                    <td style={{ padding: "2px 4px", textAlign: "center" }}>
                      <span onClick={() => removeRow(i)} title="Remove line" style={{ cursor: "pointer", color: C.muted, fontSize: 15 }}>×</span>
                    </td>
                  </tr>
                  {/* "🔍 Check stock" on pad lines lands its green strip here too. */}
                  {stockStrip(it, k, colSpanPad)}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ padding: "8px 10px", display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button onClick={() => addRow(false, null, "pad")} style={btn()}>+ Add line</button>
          <button onClick={() => addRow(true, null, "pad")} style={btn()}>+ Section header</button>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: 11.5, color: C.muted }}>
            {padStats.lines} line{padStats.lines === 1 ? "" : "s"}
            {padStats.lines ? <> · <span style={{ color: "#fde047" }}>{padStats.open} to source</span> · <span style={{ color: C.blue }}>{padStats.ordered} ordered</span> · <span style={{ color: C.green }}>{padStats.done} in hand</span></> : null}
            {" · "}tick lines → <b style={{ color: C.lime }}>📦 Order these…</b> in the green bar
          </span>
        </div>
      </div>

      {searchIdx != null && items[searchIdx] && (
        <PricebookSearchModal
          initialTerm={items[searchIdx]?.description || ""}
          onPick={(product, price) => applyPick(searchIdx, product, price)}
          onClose={() => setSearchIdx(null)}
        />
      )}

      {/* orders */}
      {orderRows.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 20 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 19, color: C.lime, marginBottom: 8 }}>Orders</div>
          {orderRows.map((o, oi, arr) => (
            <div key={o.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderBottom: "1px solid #1e2328", padding: "8px 0", fontSize: 12.5 }}>
              <span>{o.method === "email" ? "✉" : o.method === "text" ? "📱" : "☎"}</span>
              {/* Every order on a job now carries the same PO (the job #), so
                  the ordinal + branch are what tell two orders apart. The list
                  arrives created_at DESC, so count up from the bottom. */}
              {o.po_number && <span style={{ color: C.lime, fontWeight: 700 }}>{o.po_number}</span>}
              {arr.length > 1 && <span style={{ color: C.muted }}>#{arr.length - oi} of {arr.length}</span>}
              <span style={{ color: C.text, fontWeight: 700 }}>{o.branch_label || o.supplier || "?"}</span>
              <span style={{ color: C.muted }}>{o.item_count} item{o.item_count === 1 ? "" : "s"} · {o.tech_name || "?"} · {o.created_at ? new Date(o.created_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : ""}</span>
              <select value={o.status || "sent"} onChange={async e => { try { await api("updateOrder", { orderId: o.id, status: e.target.value }); await load(); } catch (er) { alert(er.message); } }}
                style={{ ...inputStyle, width: 120, border: `1px solid ${C.border}`, background: C.card }}>
                {["sent", "confirmed", "received", "partial", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              {o.expense_id
                ? <span style={chip(C.teal)}>invoice linked {o.expense_total != null ? money(o.expense_total) : ""}</span>
                : <span style={chip(C.muted)}>awaiting supplier invoice</span>}
              <details style={{ color: C.muted }}><summary style={{ cursor: "pointer" }}>message</summary><pre style={{ whiteSpace: "pre-wrap", fontSize: 11.5 }}>{o.message}</pre></details>
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>Supplier invoices on this job auto-link within minutes of the expense hitting Jobber (daily sweep as backup) — POs make that stick even when the expense has no job attached.</div>
        </div>
      )}

      {/* quote requests — grouped by blast, with somewhere for answers to land */}
      {rfqRows.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 14, marginBottom: 20 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 19, color: C.lime, marginBottom: 8 }}>Quote requests</div>
          {Object.entries(rfqRows.reduce((g, o) => { (g[o.rfq_group_id || o.id] = g[o.rfq_group_id || o.id] || []).push(o); return g; }, {})).map(([gid, rows]) => {
            const totals = rows.map(r => (r.quote_total == null ? null : Number(r.quote_total))).filter(n => n != null && !isNaN(n));
            const best = totals.length ? Math.min(...totals) : null;
            return (
              <div key={gid} style={{ borderBottom: "1px solid #1e2328", padding: "8px 0" }}>
                <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>
                  {rows[0].item_count} line{rows[0].item_count === 1 ? "" : "s"} · asked {rows.length} supplier{rows.length === 1 ? "" : "s"}
                  {rows[0].created_at ? ` · ${new Date(rows[0].created_at).toLocaleString("en-CA", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : ""}
                </div>
                {rows.map(o => (
                  <div key={o.id} style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12.5, padding: "3px 0" }}>
                    <span style={{ color: o.status === "failed" ? C.red : C.green, fontWeight: 700 }}>{o.status === "failed" ? "✗" : "✓"}</span>
                    <span style={{ color: C.text, fontWeight: 700, minWidth: 150 }}>{o.branch_label || o.supplier || "?"}</span>
                    <span style={{ color: C.muted }}>{o.method === "text" ? "📱" : "✉"} {o.recipient || "—"}</span>
                    {o.status === "failed" && <span style={{ color: C.red }}>{o.send_result?.error || "not sent"}</span>}
                    <input inputMode="decimal" defaultValue={o.quote_total ?? ""} placeholder="their quote $"
                      onBlur={async e => {
                        const raw = e.target.value.trim().replace(",", ".");
                        const n = raw === "" ? null : Number(raw);
                        if (raw !== "" && !isFinite(n)) { e.target.value = o.quote_total ?? ""; return; }
                        if ((n ?? null) === (o.quote_total ?? null)) return;
                        try { await api("updateOrder", { orderId: o.id, quote_total: n }); await load(); }
                        catch (er) { setErr(er.message); }
                      }}
                      style={{ ...inputStyle, width: 110, textAlign: "right", border: `1px solid ${C.border}`, background: C.card }} />
                    {best != null && Number(o.quote_total) === best && totals.length > 1 && <span style={chip(C.lime)}>best</span>}
                    <button style={btn()} title="Order these same lines from this supplier"
                      onClick={async () => {
                        await flushSave();
                        const want = new Set(Array.isArray(o.item_ids) ? o.item_ids : []);
                        setSelected(new Set(itemsRef.current.filter(it => it.id && want.has(it.id) && !it.is_header).map(it => it._ck || it.id)));
                        setOrdering("order");
                      }}>Order from this one</button>
                  </div>
                ))}
              </div>
            );
          })}
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>Type each supplier's number in as it comes back — the lowest gets the <b>best</b> chip. Quote requests never carry a PO and never link to an invoice.</div>
        </div>
      )}

      {/* invoices on this job that didn't match any order */}
      {unlinked.length > 0 && (
        <div style={{ background: C.surface, border: `1px solid rgba(245,158,11,.4)`, borderRadius: 14, padding: 14, marginBottom: 20 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 19, color: C.amber, marginBottom: 8 }}>Invoices on this job not linked to an order</div>
          {unlinked.map(inv => (
            <div key={inv.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderBottom: "1px solid #1e2328", padding: "7px 0", fontSize: 12.5 }}>
              <span style={{ color: C.text, fontWeight: 700 }}>{inv.supplier || "?"}</span>
              <span style={{ color: C.muted }}>{inv.invoice_number ? `#${inv.invoice_number}` : ""} {inv.invoice_date ? `· ${inv.invoice_date}` : ""}</span>
              <span style={{ color: C.teal, fontWeight: 700 }}>{money(inv.total)}</span>
              {orderRows.filter(o => !o.expense_id).length > 0 ? (
                <>
                  <select value={linkSel[inv.id] || ""} onChange={e => setLinkSel(s => ({ ...s, [inv.id]: e.target.value }))}
                    style={{ ...inputStyle, width: 210, border: `1px solid ${C.border}`, background: C.card }}>
                    <option value="">Link to order…</option>
                    {orderRows.filter(o => !o.expense_id).map(o => (
                      <option key={o.id} value={o.id}>{o.po_number ? o.po_number + " — " : ""}{o.branch_label || o.supplier} ({o.item_count})</option>
                    ))}
                  </select>
                  <button style={btn()} disabled={!linkSel[inv.id]} onClick={() => linkInv(inv.id)}>Link</button>
                </>
              ) : <span style={{ color: C.muted }}>no orders logged yet — likely a called-in or counter pickup</span>}
            </div>
          ))}
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 8 }}>Linking runs the price match too — actuals land on the rows, borderline matches show as $? chips.</div>
        </div>
      )}

      {/* block-selection pill — inside the range's region so Copy doesn't clear it */}
      {cellRange && rangeSize && (
        <RangePill rsel="takeoff" rows={rangeSize.rows} cols={rangeSize.cols} done={rangeCopied} onCopy={() => copyRangeRef.current(cellRange)} onClear={clearRange} />
      )}

      <datalist id="takeoff-suppliers">{suppliers.map(s => <option key={s} value={s} />)}</datalist>
      <datalist id="takeoff-locations">{LOCATION_SUGGESTIONS.map(l => <option key={l} value={l} />)}</datalist>

      {ordering === "order" && selItems.length > 0 && (
        <OrderDrawer sheet={sheet} items={selItems} branches={branches} tech={tech}
          /* Mirrors the server mint exactly. The old form counted existing
             orders client-side, so the drawer promised "-01" while the server
             minted "-02" if another order landed since this page loaded. */
          poPreview={sheet.job_number ? String(sheet.job_number) : `BETK${String(sheet.id).replace(/-/g, "").slice(0, 5).toUpperCase()}`}
          onClose={() => setOrdering(null)}
          onDone={async () => { setOrdering(null); setSelected(new Set()); await load(); }} />
      )}
      {ordering === "rfq" && selItems.length > 0 && (
        <QuoteRequestDrawer sheet={sheet} items={selItems} branches={branches} tech={tech}
          onClose={() => setOrdering(null)}
          /* Selection is kept: a quote request doesn't consume the lines, and
             the usual next step is ordering the same ones from the winner. */
          onDone={async () => { setOrdering(null); await load(); }} />
      )}
      {showDirectory && (
        <BranchDirectory branches={branches} onClose={() => setShowDirectory(false)}
          onSaved={async () => { const r = await api("listSuppliers"); setSuppliers(r.suppliers || []); setBranches(r.branches || []); }} />
      )}
      {showVanStock && <VanStockModal tech={tech} onClose={() => setShowVanStock(false)} />}
      {pkgSave && (
        <SavePackageModal sheet={sheet} snapshot={pkgSave} tech={tech}
          onDone={(m) => { setPkgSave(null); setSelected(new Set()); setMsg(m); }}
          onClose={() => setPkgSave(null)} />
      )}
      {pkgAdd && (
        <AddPackageModal sheetId={sheetId} tech={tech} beforeMutate={flushSave}
          onDone={async (m) => { setPkgAdd(false); setMsg(m); clearFilters(); await load(); }}
          onClose={() => setPkgAdd(false)} />
      )}
    </div>
  );
}

// ═══════════════════════════ material packages ═══════════════════════════════
// Save the selected takeoff lines as a named, reusable bundle ("26kW Generac
// power cable terminations"). Optionally give it an auto-add phrase (future
// quote drafts whose description matches get the lines folded in before
// pricing) and/or attach it to a saved quote template (using that template
// always folds the package in) — templates slowly accumulate the materials
// real jobs actually needed.
function SavePackageModal({ sheet, snapshot, tech, onDone, onClose }) {
  const [name, setName] = useState(snapshot.defaultName || "");
  const [autoOn, setAutoOn] = useState(false);
  const [autoApply, setAutoApply] = useState("");
  const [templateId, setTemplateId] = useState("");
  const [templates, setTemplates] = useState(null); // null = loading
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let dead = false;
    (async () => {
      try {
        const r = await apiFetch("/api/quote-brain", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "listQuoteTemplates" }),
        });
        const j = await r.json().catch(() => ({}));
        if (!dead) setTemplates(Array.isArray(j.templates) ? j.templates : []);
      } catch { if (!dead) setTemplates([]); }
    })();
    return () => { dead = true; };
  }, []);

  async function save(overwrite = false) {
    const nm = name.trim();
    if (!nm) { setErr("Name the package first — e.g. “26kW Generac power cable terminations”."); return; }
    setBusy(true); setErr(null);
    try {
      // Raw fetch (not the throwing api() helper): a same-name clash comes
      // back as ok:false + clash:true and must turn into an overwrite prompt,
      // not an error toast.
      const r = await apiFetch("/api/takeoff", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "savePackage", sheetId: sheet.id, name: nm, items: snapshot.items,
          autoApply: autoOn ? autoApply.trim() : "",
          ...(templateId ? { templateId } : {}), techName: tech.name, overwrite,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (j.clash && !overwrite) {
        setBusy(false);
        if (window.confirm(`A package named "${nm}" already exists — replace its lines with this selection?`)) return save(true);
        return;
      }
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      onDone(`Package “${j.name}” saved — ${j.items ?? snapshot.items.length} line${(j.items ?? snapshot.items.length) === 1 ? "" : "s"}`
        + (j.attachedToTemplate ? ` · attached to template “${j.attachedToTemplate}”` : "")
        + (autoOn && autoApply.trim() ? " · will auto-add to matching quotes" : "")
        + (j.templateError ? ` · ⚠ ${j.templateError}` : "") + " ✓");
    } catch (e) { setErr(e.message); setBusy(false); }
  }

  const label = { fontSize: 11.5, color: C.muted, margin: "10px 0 4px" };
  const boxInput = { ...inputStyle, border: `1px solid ${C.border}`, background: C.card };
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 70, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 16px 16px" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px, 100%)", maxHeight: "80vh", overflowY: "auto", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: C.lime, flex: 1 }}>Save material package</div>
          <button style={btn()} onClick={onClose}>✕</button>
        </div>
        <div style={{ fontSize: 12.5, color: C.muted, marginBottom: 4 }}>
          {snapshot.items.length} selected line{snapshot.items.length === 1 ? "" : "s"} — build the list once, reuse it forever.
        </div>

        <div style={label}>Package name</div>
        <input autoFocus value={name} onChange={e => setName(e.target.value)} placeholder={"e.g. 26kW Generac power cable terminations"} style={boxInput} />

        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 10px", margin: "10px 0", maxHeight: 150, overflowY: "auto" }}>
          {snapshot.items.slice(0, 12).map((it, i) => (
            <div key={i} style={{ fontSize: 12, color: C.text, padding: "2px 0", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              <span style={{ color: C.muted }}>{it.qty ?? 1} {it.unit || "ea"} · </span>{it.description}
              {it.part_number ? <span style={{ color: C.muted }}> [{it.part_number}]</span> : null}
            </div>
          ))}
          {snapshot.items.length > 12 && <div style={{ fontSize: 11.5, color: C.muted, paddingTop: 2 }}>+ {snapshot.items.length - 12} more…</div>}
        </div>

        <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, color: C.text, cursor: "pointer" }}>
          <input type="checkbox" checked={autoOn} onChange={e => setAutoOn(e.target.checked)} />
          Auto-add to future quotes that match a phrase
        </label>
        {autoOn && (
          <>
            <input value={autoApply} onChange={e => setAutoApply(e.target.value)} placeholder={"e.g. 26kw generator"} style={{ ...boxInput, marginTop: 6 }} />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              Any future quote draft whose scope / description contains every word of the phrase (any order — "26kw" also matches "26 kW") gets these lines added before pricing. Comma separates alternative phrasings.
            </div>
          </>
        )}

        <div style={label}>Also attach to a saved quote template (optional)</div>
        <select value={templateId} onChange={e => setTemplateId(e.target.value)} style={{ ...boxInput, color: templateId ? C.text : C.muted }}>
          <option value="">{templates === null ? "Loading templates…" : templates.length ? "No — just save the package" : "No saved quote templates yet"}</option>
          {(templates || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
          Attached packages fold into every quote made from that template — the template keeps getting more dialed from real job takeoffs.
        </div>

        {err && <div style={{ color: C.red, fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button style={btn()} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={{ ...btn(true), opacity: busy ? .6 : 1 }} disabled={busy} onClick={() => save(false)}>{busy ? "Saving…" : "Save package"}</button>
        </div>
      </div>
    </div>
  );
}

// Pick saved packages (one or several) and drop each onto the sheet as its own
// section header + lines. Light management lives here too: edit the auto-add
// phrase, archive (frees the name; existing sheets keep their rows).
function AddPackageModal({ sheetId, tech, beforeMutate, onDone, onClose }) {
  const [pkgs, setPkgs] = useState(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api("listPackages"); setPkgs(r.packages || []); }
    catch (e) { setErr(e.message); setPkgs([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const shown = (pkgs || []).filter(p => !q.trim() || String(p.name).toLowerCase().includes(q.trim().toLowerCase()));

  async function add() {
    if (!sel.size || busy) return;
    setBusy(true); setErr(null);
    try {
      await beforeMutate?.(); // a stale autosave firing after the append would revert it
      const r = await api("addPackages", { sheetId, packageIds: [...sel], techName: tech.name });
      onDone(`Added ${r.packages} package${r.packages === 1 ? "" : "s"} — ${r.added - r.packages} line${r.added - r.packages === 1 ? "" : "s"} under ${r.packages === 1 ? "its header" : "their headers"} ✓`);
    } catch (e) { setErr(e.message); setBusy(false); }
  }
  async function archive(p) {
    if (!window.confirm(`Archive “${p.name}”? It leaves this list and stops auto-adding to quotes — sheets that already used it keep their rows.`)) return;
    try { await api("updatePackage", { packageId: p.id, patch: { archived: true } }); setSel(prev => { const n = new Set(prev); n.delete(p.id); return n; }); await load(); }
    catch (e) { alert(e.message); }
  }
  async function editTrigger(p) {
    const v = window.prompt('Auto-add phrase (leave empty for manual-only). Future quotes whose description matches get this package — e.g. "26kw generator". Comma separates alternatives.', p.auto_apply || "");
    if (v === null) return;
    try { await api("updatePackage", { packageId: p.id, patch: { auto_apply: v } }); await load(); }
    catch (e) { alert(e.message); }
  }
  // Where the auto-add lands: 'quote' = folds into the quote draft at Convert;
  // 'takeoff' = skips the quote and lands on the job's takeoff sheet at
  // convert, under a ⚠️-verify header (the Generac bundles live there so the
  // quote backend stays cables + misc).
  async function toggleStage(p) {
    const takeoffOnly = String(p.fold_stage || "quote") === "takeoff";
    const msg = takeoffOnly
      ? `“${p.name}” currently auto-adds at JOB CONVERT only (takeoff sheet, flagged for verify). Switch it back to folding into QUOTE drafts?`
      : `“${p.name}” currently folds into QUOTE drafts. Switch it to JOB CONVERT only — the quote stays clean, and the parts land on the takeoff sheet flagged for the crew to verify?`;
    if (!window.confirm(msg)) return;
    try { await api("updatePackage", { packageId: p.id, patch: { fold_stage: takeoffOnly ? "quote" : "takeoff" } }); await load(); }
    catch (e) { alert(e.message); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 70, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 16px 16px" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(640px, 100%)", maxHeight: "80vh", overflowY: "auto", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: C.lime, flex: 1 }}>Add material package{sel.size > 1 ? "s" : ""}</div>
          <button style={btn()} onClick={onClose}>✕</button>
        </div>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search packages…" autoFocus
          style={{ ...inputStyle, border: `1px solid ${C.border}`, background: C.card, marginBottom: 10 }} />

        {pkgs === null && <div style={{ color: C.muted, fontSize: 12.5 }}>Loading packages…</div>}
        {pkgs !== null && !shown.length && (
          <div style={{ color: C.muted, fontSize: 12.5, padding: "8px 0" }}>
            {pkgs.length ? "No package matches that search." : "No packages yet — select lines on a takeoff and hit “🧰 Save as package”."}
          </div>
        )}
        {shown.map(p => (
          <div key={p.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderBottom: "1px solid #1e2328", padding: "8px 2px", fontSize: 12.5 }}>
            <input type="checkbox" checked={sel.has(p.id)}
              onChange={() => setSel(prev => { const n = new Set(prev); if (n.has(p.id)) n.delete(p.id); else n.add(p.id); return n; })} />
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ color: C.text, fontWeight: 700 }}>{p.name}</div>
              <div style={{ color: C.muted, fontSize: 11.5 }}>
                {p.item_count} line{p.item_count === 1 ? "" : "s"}
                {p.source_job_number ? ` · from job #${p.source_job_number}` : ""}
                {p.use_count ? ` · used ${p.use_count}×` : ""}
              </div>
            </div>
            {p.auto_apply
              ? <span style={chip(C.lime)} title={`Auto-adds to quotes matching: ${p.auto_apply}`}>auto: {String(p.auto_apply).slice(0, 26)}{String(p.auto_apply).length > 26 ? "…" : ""}</span>
              : <span style={chip(C.muted)}>manual</span>}
            {String(p.fold_stage || "quote") === "takeoff" && (
              <span style={{ ...chip(C.amber), cursor: "pointer" }} onClick={() => toggleStage(p)}
                title="Skips the quote — auto-adds to the job's takeoff sheet at convert, under a ⚠️ verify header. Click to switch back to quote-time folding.">
                ⚠️ job convert only
              </span>
            )}
            {p.auto_apply && String(p.fold_stage || "quote") !== "takeoff" && (
              <span style={{ ...chip(C.muted), cursor: "pointer" }} onClick={() => toggleStage(p)}
                title="Folds into quote drafts at Convert. Click to switch to job-convert-only (quote stays clean; parts land on the takeoff flagged for verify).">
                → quote
              </span>
            )}
            <button style={{ ...btn(), padding: "3px 8px", fontSize: 11.5 }} onClick={() => editTrigger(p)} title="Edit the auto-add phrase">✎</button>
            <button style={{ ...btn(false, true), padding: "3px 8px", fontSize: 11.5 }} onClick={() => archive(p)} title="Archive this package">🗑</button>
          </div>
        ))}

        {err && <div style={{ color: C.red, fontSize: 12.5, marginTop: 8 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, marginTop: 14, justifyContent: "flex-end" }}>
          <button style={btn()} onClick={onClose} disabled={busy}>Cancel</button>
          <button style={{ ...btn(true), opacity: busy || !sel.size ? .6 : 1 }} disabled={busy || !sel.size} onClick={add}>
            {busy ? "Adding…" : `Add ${sel.size || ""} package${sel.size === 1 ? "" : "s"} to this takeoff`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════ shell ═══════════════════════════════════
export default function MaterialTakeoff({ initialSheetId = null }) {
  const perms = usePermissions() || {};
  const [sheetId, setSheetId] = useState(initialSheetId);
  return sheetId
    ? <SheetDetail sheetId={sheetId} onBack={() => setSheetId(null)} perms={perms} />
    : <SheetList onOpen={setSheetId} />;
}

// Standalone page for the /takeoff sidebar route (same container treatment as
// QuotesPage — the component itself is also mounted as a tab inside Quotes).
// /takeoff?sheet=<uuid> deep-links straight into that sheet (the FlowStrip's
// Takeoff chip uses this); key= forces a remount when the param changes so a
// second deep-link from another page doesn't land on the stale sheet.
export function TakeoffPage() {
  const [params] = useSearchParams();
  const sheetParam = params.get("sheet") || null;
  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 16px 8px", color: C.text, fontFamily: "'Roboto', sans-serif" }}>
      <MaterialTakeoff key={sheetParam || "list"} initialSheetId={sheetParam} />
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Roboto:wght@400;600;700&display=swap');
      `}</style>
    </div>
  );
}
